import React, { useState, useEffect } from 'react';
import { View } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { LOCATIONS } from '../../data/locations';
import { formatGenericID, todayLocal } from '../../utils/formatters';
import {
    Dog, QrCode, ShieldCheck,
    Activity, Award, ArrowRight, Settings,
    Share2, FileText, CheckCircle, Plus, Camera,
    Trash2, Save, X, Info, Heart, Search,
    Syringe, Stethoscope, Files, ExternalLink, Upload,
    UserPlus, Loader2, Baby, Copy, Download, Clock, LogOut, FileSignature, ListChecks, Eye, EyeOff,
    LayoutDashboard, Bell, Image as ImageIcon
} from 'lucide-react';
import { api } from '../../services/api';
import { QRCodeSVG } from 'qrcode.react';
import { BreederPetHealth } from './BreederPetHealth';
import { BreederLitters } from './BreederLitters';
import { BreederHome } from './BreederHome';
import { ParentSelectionModal, ManadaView, PedigreeView } from './BreederViews';
import { GrowthChart } from './GrowthChart';
import { BreederWaitlist } from './BreederWaitlist';
import { BreederContracts } from './BreederContracts';
import { BreederUpgradeModal, proDaysLeft, type BreederPlanInfo } from './BreederUpgrade';

// Breeds list
import { SPECIES_CONFIG } from '../../data/species';
const breeds = SPECIES_CONFIG['Perro']?.breeds || [];

interface BreederDashboardProps {
    onExit: (view: View) => void;
    monitoringUser?: any;
}

export const BreederDashboard: React.FC<BreederDashboardProps> = ({ onExit, monitoringUser }) => {
    const { user: authUser } = useAuth();
    const user = monitoringUser || authUser;
    const [activeTab, setActiveTab] = useState<'dashboard' | 'manada' | 'litters' | 'pedigree' | 'transfers' | 'waitlist' | 'contracts' | 'docs' | 'settings' | 'pet_detail'>('dashboard');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [breederProfile, setBreederProfile] = useState<any>(null);
    const [inventory, setInventory] = useState<any[]>([]);
    const [transfers, setTransfers] = useState<any[]>([]);
    const [litters, setLitters] = useState<any[]>([]);
    const [heats, setHeats] = useState<any[]>([]);
    const [kennelDocs, setKennelDocs] = useState<any[]>([]);
    const [validations, setValidations] = useState<any[]>([]);
    const [loadError, setLoadError] = useState('');
    // Plan Gratis / Pro y aviso de límite ("upgradeReason" abre el modal de Criador Pro).
    const [planInfo, setPlanInfo] = useState<BreederPlanInfo | null>(null);
    const [upgradeReason, setUpgradeReason] = useState<string | null>(null);
    const [upgradeNeed, setUpgradeNeed] = useState<'pro' | 'elite'>('pro');
    const askElite = (msg: string) => { setUpgradeNeed('elite'); setUpgradeReason(msg); };
    const askPro = (msg: string) => { setUpgradeNeed('pro'); setUpgradeReason(msg); };
    const [transferError, setTransferError] = useState('');
    const [transferBusy, setTransferBusy] = useState(false);
    // Un root/admin monitoreando a un criadero opera "como" ese usuario (el servidor lo valida).
    const asUser = monitoringUser?.id;
    const [selectedQrPet, setSelectedQrPet] = useState<any>(null);
    const [selectedPetDetail, setSelectedPetDetail] = useState<any>(null);
    const [petGrowthLogs, setPetGrowthLogs] = useState<any[]>([]);
    const [petGallery, setPetGallery] = useState<any[]>([]);
    const [showValidationRequestModal, setShowValidationRequestModal] = useState(false);
    const [validationReqData, setValidationReqData] = useState({ pedigreeReg: '', document: null as File | null });
    const [isSubmittingValidation, setIsSubmittingValidation] = useState(false);
    const [activeDetailTab, setActiveDetailTab] = useState<'growth' | 'health' | 'history' | 'docs' | 'galeria'>('growth');
    const [petDocs, setPetDocs] = useState<any[]>([]);
    const [showAddDocModal, setShowAddDocModal] = useState(false);
    const [docFormData, setDocFormData] = useState({ title: '', type: 'Examen de Salud', file: null as File | null });
    const [successModal, setSuccessModal] = useState<{ isOpen: boolean, title: string, message: string }>({ isOpen: false, title: '', message: '' });
    const [loading, setLoading] = useState(false);
    const [growthLogForm, setGrowthLogForm] = useState({ weight: '', height: '', note: '' });


    // Adoption Search States
    const [showAdoptionModal, setShowAdoptionModal] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [foundUsers, setFoundUsers] = useState<any[]>([]);
    const [isSearchingUsers, setIsSearchingUsers] = useState(false);
    const [selectedAdoptionPet, setSelectedAdoptionPet] = useState<any>(null);
    const [transferEmail, setTransferEmail] = useState('');
    const [transferPet, setTransferPet] = useState<any>(null);
    const [showTransferModal, setShowTransferModal] = useState(false);

    const [settingsFormData, setSettingsFormData] = useState({
        kennelName: '',
        country: 'Chile',
        region: '',
        comuna: '',
        location: '',
        specialty: '',
        description: '',
        phone: '',
        email: '',
        manager_rut: '',
        logoUrl: '',
        bannerUrl: ''
    });

    const loadInventory = async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            // Manada real: ejemplares del criadero con estado, padres, chip y pedigrí ya resueltos por el servidor.
            const data = await api.breeder.getManada(asUser);
            setInventory(data || []);
            // Si hay un ejemplar abierto, se refresca con los datos nuevos (estado, pedigrí…).
            setSelectedPetDetail((prev: any) => prev ? (data || []).find((p: any) => String(p.id) === String(prev.id)) || prev : prev);
        } catch (error: any) {
            console.error("Error loading manada:", error);
            setLoadError(error.message || 'No se pudo cargar la manada');
        } finally {
            setLoading(false);
        }
    };

    const loadLitters = async () => {
        try {
            const [l, h] = await Promise.all([api.breeder.getLitters(asUser), api.breeder.getHeats(asUser)]);
            setLitters(l); setHeats(h);
        } catch (e) { console.error('Error loading litters:', e); }
    };
    const loadTransfers = async () => {
        try { setTransfers(await api.breeder.getTransfers(asUser)); } catch (e) { console.error('Error loading transfers:', e); }
    };
    const loadKennelDocs = async () => {
        try {
            const r = await api.breeder.getDocs(asUser);
            setKennelDocs(r.docs); setValidations(r.validations);
        } catch (e) { console.error('Error loading docs:', e); }
    };
    const loadPlan = async () => {
        try { setPlanInfo(await api.breeder.getPlan(asUser)); } catch (e) { console.error('Error loading plan:', e); }
    };
    const reloadAll = async () => { await Promise.all([loadInventory(), loadLitters(), loadTransfers(), loadKennelDocs(), loadPlan()]); };
    // Lo que el plan permite en fotos y documentos por ejemplar (el resto lo valida el servidor).
    const photoMax = planInfo?.limits.photos ?? 5;
    const docMax = planInfo?.limits.docs ?? (planInfo?.plan === 'pro' ? Infinity : 3);

    const loadPetDocs = async (petId: any) => {
        try {
            const data = await api.getPetDocuments(petId);
            setPetDocs(data || []);
        } catch (e) {
            console.error("Error loading pet docs:", e);
        }
    };

    const loadBreederProfile = async () => {
        if (!user?.id) return;
        try {
            const res = await api.breeder.checkStatus(asUser);
            const b = res.breeder;
            const profile = {
                kennelName: b?.kennel_name || user.name || "Mi Criadero",
                location: b?.location || (user.city ? `${user.city}, ${user.region}` : ""),
                specialty: b?.specialty_breeds || "",
                description: b?.description || "",
                phone: b?.phone || "",
                email: b?.email || user.email,
                verified: !!b?.verified,
                slug: b?.slug || '',
                logoUrl: b?.logo_url || user.imageUrl || "",
                bannerUrl: b?.banner_url || ""
            };
            setBreederProfile(profile);
            setSettingsFormData({
                kennelName: profile.kennelName,
                country: b?.country || user.country || 'Chile',
                region: b?.region || user.region || '',
                comuna: b?.city || user.city || '',
                location: profile.location,
                specialty: profile.specialty,
                description: profile.description,
                phone: profile.phone,
                email: profile.email,
                manager_rut: b?.manager_rut || '',
                logoUrl: profile.logoUrl,
                bannerUrl: profile.bannerUrl
            });
        } catch (e: any) {
            console.error(e);
            setLoadError(e.message || 'No se pudo cargar el perfil del criadero');
        }
    };

    const handleSaveSettings = async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            await api.breeder.updateProfile(settingsFormData, asUser);

            setBreederProfile((prev: any) => ({
                ...prev,
                kennelName: settingsFormData.kennelName,
                specialty: settingsFormData.specialty,
                description: settingsFormData.description,
                phone: settingsFormData.phone,
                email: settingsFormData.email,
                location: settingsFormData.location || [settingsFormData.comuna, settingsFormData.region].filter(Boolean).join(', ')
            }));

            setSuccessModal({
                isOpen: true,
                title: 'Perfil Actualizado',
                message: 'La información del criadero ha sido guardada con éxito.'
            });
        } catch (e: any) {
            console.error(e);
            alert("Error al guardar: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveGrowthLog = async () => {
        if (!selectedPetDetail?.id || !growthLogForm.weight) return;
        setLoading(true);
        try {
            await api.addGrowthRecord({
                petId: selectedPetDetail.id,
                date: todayLocal(),
                weight: parseFloat(growthLogForm.weight) || 0,
                height: parseFloat(growthLogForm.height) || 0,
                note: growthLogForm.note || 'Actualización de criadero'
            });

            // Reload logs
            await loadGrowthLogs(selectedPetDetail.id);
            setGrowthLogForm({ weight: '', height: '', note: '' });

            setSuccessModal({
                isOpen: true,
                title: 'Evolución Guardada',
                message: 'El registro de crecimiento ha sido actualizado exitosamente.'
            });
        } catch (e: any) {
            console.error(e);
            alert("Error al guardar evolución: " + (e.message || String(e)));
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (user?.id) {
            loadBreederProfile();
            reloadAll();
        }
    }, [user?.id, asUser]);

    const loadGrowthLogs = async (petId: any) => {
        try {
            const data = await api.getGrowthRecords(petId);
            setPetGrowthLogs(data || []);
        } catch (e) {
            console.error("Error loading growth logs:", e);
        }
    };

    const loadPetGallery = async (petId: any) => {
        try {
            const data = await api.getPetGallery(petId);
            setPetGallery(data || []);
        } catch (e) {
            console.error("Error loading pet gallery:", e);
        }
    };

    const handleUploadGalleryImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0] || !selectedPetDetail) return;
        const file = e.target.files[0];
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'gallery');
            const res = await fetch(`${api.API_URL}/upload.php`, { method: 'POST', body: formData });
            const data = await res.json();
            if (data.success) {
                await api.addPetGalleryImage({ petId: String(selectedPetDetail.id), url: data.url });
                await loadPetGallery(selectedPetDetail.id);
            } else {
                alert('Error al subir imagen: ' + data.error);
            }
        } catch (error: any) {
            console.error(error);
            alert('Error de red: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateProfilePicture = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0] || !selectedPetDetail) return;
        const file = e.target.files[0];
        setLoading(true);
        try {
            const res = await api.uploadImage(file);
            if (res.success) {
                await api.updatePet(selectedPetDetail.id, { imageUrl: res.url });
                // Update local state
                setSelectedPetDetail({ ...selectedPetDetail, imageUrl: res.url });
                // Update inventory list
                setInventory(prev => prev.map(p => p.id === selectedPetDetail.id ? { ...p, imageUrl: res.url } : p));
            } else {
                alert('Error al subir imagen: ' + res.message);
            }
        } catch (error: any) {
            console.error(error);
            alert('Error de red: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUploadBreederImage = async (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'banner') => {
        if (!e.target.files || !e.target.files[0] || !user?.id) return;
        const file = e.target.files[0];
        setLoading(true);
        try {
            const res = await api.uploadImage(file);
            if (res.success) {
                if (type === 'logo') {
                    setSettingsFormData(prev => ({ ...prev, logoUrl: res.url }));
                    setBreederProfile((prev: any) => ({ ...prev, logoUrl: res.url }));
                } else {
                    setSettingsFormData(prev => ({ ...prev, bannerUrl: res.url }));
                    setBreederProfile((prev: any) => ({ ...prev, bannerUrl: res.url }));
                }
            } else {
                alert('Error al subir imagen: ' + res.message);
            }
        } catch (error: any) {
            console.error(error);
            alert('Error de red: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteGalleryImage = async (imageId: string | number) => {
        if (!confirm('¿Seguro de eliminar esta imagen?')) return;
        setLoading(true);
        try {
            await api.deletePetGalleryImage(imageId);
            if (selectedPetDetail) await loadPetGallery(selectedPetDetail.id);
        } catch (error) {
            console.error(error);
            alert('Error al eliminar imagen');
        } finally {
            setLoading(false);
        }
    };


    const handleUploadDocument = async () => {
        if (!docFormData.title || !docFormData.file || !selectedPetDetail) return alert("Completa todos los campos");
        if (petDocs.length >= docMax) { setShowAddDocModal(false); setUpgradeReason(`El plan Gratis permite hasta ${docMax} documentos por ejemplar. Con Criador Pro son ilimitados.`); return; }

        setLoading(true);
        try {
            // 1. Upload file
            const formData = new FormData();
            formData.append('file', docFormData.file);
            formData.append('type', 'document');

            const uploadRes = await fetch(`${api.API_URL}/upload.php`, {
                method: 'POST',
                body: formData
            });
            const text = await uploadRes.text();
            let uploadResult;
            try {
                uploadResult = JSON.parse(text);
            } catch (e) {
                throw new Error("Respuesta inválida del servidor al subir");
            }

            if (!uploadResult.success) throw new Error(uploadResult.error || "Error al subir archivo");

            // 2. Save record
            await api.addPetDocument({
                petId: selectedPetDetail.id,
                title: docFormData.title,
                document_type: docFormData.type,
                file_url: uploadResult.url
            });

            await loadPetDocs(selectedPetDetail.id);
            setShowAddDocModal(false);
            setDocFormData({ title: '', type: 'Examen de Salud', file: null as File | null });

            setSuccessModal({
                isOpen: true,
                title: 'Documento Guardado',
                message: 'El documento ha sido anexado exitosamente al expediente del ejemplar.'
            });
        } catch (e: any) {
            console.error(e);
            alert(`Error al subir documento: ${e.message || 'Error desconocido'}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchUsers = async (q: string) => {
        setUserSearchTerm(q);
        if (q.length < 3) {
            setFoundUsers([]);
            return;
        }
        setIsSearchingUsers(true);
        try {
            const res = await api.searchUsers(q);
            setFoundUsers(res.users || []);
        } catch (e) {
            console.error(e);
        } finally {
            setIsSearchingUsers(false);
        }
    };

    const handleStartAdoption = (pet: any, targetUser: any) => {
        setTransferPet(pet);
        setTransferError('');
        setTransferEmail(targetUser.email);
        setShowAdoptionModal(false);
        setShowTransferModal(true);
    };

    const [showNewPetModal, setShowNewPetModal] = useState(false);
    const [showPedigreeModal, setShowPedigreeModal] = useState(false);
    const EMPTY_NEW_PET = {
        name: '', breed: '', gender: 'Macho', imageUrl: '', status: 'disponible', microchip: '',
        birthWeight: '', birthHeight: '', notes: '', fatherId: '', motherId: '', pedigreeReg: '',
        birthDate: '', litterId: ''
    };
    const [newPetData, setNewPetData] = useState(EMPTY_NEW_PET);

    const [parentSelectionModal, setParentSelectionModal] = useState<{ isOpen: boolean, type: 'padre' | 'madre' | null }>({ isOpen: false, type: null });

    // Cualquier macho/hembra que siga en el criadero puede ser padre/madre (antes solo los
    // marcados "criadero", y como el estado nunca se guardaba la lista salía siempre vacía).
    const stillHere = inventory.filter(p => p.owned !== false && p.status !== 'entregado');
    const fathers = stillHere.filter(p => p.gender === 'Macho' || p.gender === 'Male');
    const mothers = stillHere.filter(p => p.gender === 'Hembra' || p.gender === 'Female');

    // "+ Cachorro" desde una camada: abre el alta con padres y fecha de nacimiento ya puestos.
    const startPuppyFromLitter = (l: any) => {
        setNewPetData({
            ...EMPTY_NEW_PET, litterId: String(l.id), birthDate: l.birthDate || '',
            fatherId: l.fatherId || (l.fatherName ? 'external' : ''), motherId: l.motherId || (l.motherName ? 'external' : ''),
            breed: (l.motherId && inventory.find(p => String(p.id) === String(l.motherId))?.breed) || '',
        });
        setShowNewPetModal(true);
    };

    // Health Record Modals
    const [showVaccineModal, setShowVaccineModal] = useState(false);
    const [showConsultationModal, setShowConsultationModal] = useState(false);
    const [healthFormData, setHealthFormData] = useState<any>({});
    const [tempPrescriptions, setTempPrescriptions] = useState<any[]>([]);
    const [prescriptionForm, setPrescriptionForm] = useState({
        name: '',
        start: '09:00',
        freq: '8',
        duration: '5'
    });
    const [consultationErrors, setConsultationErrors] = useState<Record<string, boolean>>({});
    const [editingConsultId, setEditingConsultId] = useState<string | null>(null);

    const handleAddNewPet = async () => {
        if (!newPetData.name || !newPetData.birthDate) return alert("Nombre y Fecha de Nacimiento son obligatorios");

        setLoading(true);
        try {
            // El servidor confirma que el plan permite un ejemplar más antes de crearlo.
            await api.breeder.checkCanAddPet(asUser);
            // 'external' es solo una etiqueta de la interfaz: no es un ID de ejemplar.
            const parentId = (v: any) => (v && v !== 'external' ? v : '');
            const fatherId = parentId(newPetData.fatherId);
            const motherId = parentId(newPetData.motherId);
            const ownerEmail = monitoringUser?.email || user?.email;

            const response = await api.addPet({
                name: newPetData.name.trim(),
                breed: newPetData.breed,
                gender: newPetData.gender,
                birthDate: newPetData.birthDate,
                chip_number: newPetData.microchip,
                fatherId, motherId,
                userId: ownerEmail, // el servidor lo resuelve al ID del criador
                species: 'Perro', // el módulo de criadero trabaja con perros
            });

            const newId = response?.pet?.id;
            if (response.message !== 'EXITO' || !newId) throw new Error(response?.error || 'El servidor no confirmó el registro');

            // Datos propios del criadero (estado de venta, camada, pedigrí declarado) — los guarda breeder.php.
            await api.breeder.setPetData({
                petId: newId, status: newPetData.status || 'disponible', litterId: newPetData.litterId,
                pedigreeReg: newPetData.pedigreeReg, microchip: newPetData.microchip, fatherId, motherId,
            }, asUser);

            // Peso/talla al nacer = primer punto de la curva de crecimiento.
            if (newPetData.birthWeight || newPetData.birthHeight) {
                await api.addGrowthRecord({
                    petId: newId, date: newPetData.birthDate,
                    weight: parseFloat(newPetData.birthWeight) || 0, height: parseFloat(newPetData.birthHeight) || 0,
                    note: 'Registro al nacer'
                }).catch((e: any) => console.error('No se pudo guardar el registro al nacer', e));
            }

            const savedName = newPetData.name.trim();
            setShowNewPetModal(false);
            setNewPetData(EMPTY_NEW_PET);
            await reloadAll();

            setSuccessModal({
                isOpen: true,
                title: '¡Bienvenido a la Manada!',
                message: `<strong>${savedName.replace(/</g, '&lt;')}</strong> ha sido registrado exitosamente. Su carnet sanitario ya está listo para seguimiento.`
            });
        } catch (e: any) {
            console.error(e);
            if (e.code === 'PLAN_LIMIT') { setShowNewPetModal(false); setUpgradeReason(e.message); loadPlan(); }
            else alert("Error al registrar el ejemplar: " + (e.message || 'intenta nuevamente'));
        } finally {
            setLoading(false);
        }
    };

    const handleAddVaccine = async () => {
        if (!healthFormData.name || !healthFormData.date || !selectedPetDetail) return alert("Completa los campos obligatorios");
        setLoading(true);
        try {
            await api.addVaccine({
                pet_id: selectedPetDetail.id,
                name: healthFormData.name,
                date_applied: healthFormData.date,
                place: healthFormData.place || 'Criadero Amigo Peludo'
            });
            setSuccessModal({
                isOpen: true,
                title: 'Vacuna Registrada',
                message: 'La información se ha guardado en el carnet sanitario. Estos datos serán transferidos automáticamente al dueño final para asegurar la continuidad del calendario sanitario.'
            });
            setShowVaccineModal(false);
            setHealthFormData({});
            // Reload details to refresh BreederPetHealth
            const updated = await api.getPetById(selectedPetDetail.id);
            if (updated) setSelectedPetDetail(updated);
        } catch (e) {
            console.error(e);
            alert("Error al registrar vacuna");
        } finally {
            setLoading(false);
        }
    };

    const handleAddConsultation = async () => {
        const errors: Record<string, boolean> = {};
        if (!healthFormData.type) errors.type = true;
        if (!healthFormData.date) errors.date = true;
        if (!healthFormData.diagnosis) errors.diagnosis = true;
        if (!healthFormData.place) errors.place = true;

        if (Object.keys(errors).length > 0) {
            setConsultationErrors(errors);
            return;
        }

        setLoading(true);
        setConsultationErrors({});
        try {
            const payload = {
                petId: selectedPetDetail.id,
                date: healthFormData.date,
                type: healthFormData.type,
                diagnosis: healthFormData.diagnosis,
                recommendation: healthFormData.recommendation,
                doctor: healthFormData.doctor,
                clinic: healthFormData.place || 'Control Criadero',
                prescriptions: tempPrescriptions
            };
            if (editingConsultId) await api.updateHealthRecord(editingConsultId, payload);
            else await api.addHealthRecord(payload);

            setSuccessModal({
                isOpen: true,
                title: editingConsultId ? 'Consulta Actualizada' : 'Consulta Guardada',
                message: 'El historial médico ha sido actualizado. El seguimiento de salud es vital desde las primeras semanas de vida.'
            });
            setShowConsultationModal(false);
            setHealthFormData({});
            setTempPrescriptions([]);
            setEditingConsultId(null);
            const updated = await api.getPetById(selectedPetDetail.id);
            if (updated) setSelectedPetDetail(updated);
        } catch (e: any) {
            console.error(e);
            alert(`Error al registrar consulta: ${e.message || 'Error desconocido'}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEditConsultation = (consult: any) => {
        setEditingConsultId(String(consult.id));
        setHealthFormData({
            date: String(consult.date || '').slice(0, 10),
            type: consult.type || 'Control',
            place: consult.clinic || '',
            doctor: consult.doctor || '',
            diagnosis: consult.diagnosis || '',
            recommendation: consult.recommendation || ''
        });
        setTempPrescriptions(Array.isArray(consult.prescriptions) ? consult.prescriptions : []);
        setConsultationErrors({});
        setShowConsultationModal(true);
    };

    const handleDeleteConsultation = async (consult: any) => {
        if (!confirm('¿Eliminar este registro del historial médico?')) return;
        try {
            await api.deleteHealthRecord(String(consult.id));
            const updated = await api.getPetById(selectedPetDetail.id);
            if (updated) setSelectedPetDetail(updated);
        } catch (e: any) {
            alert(`No se pudo eliminar: ${e.message || 'intenta nuevamente'}`);
        }
    };

    const handleAddPrescription = () => {
        if (!prescriptionForm.name) return;
        const newP = {
            id: Date.now(),
            ...prescriptionForm
        };
        setTempPrescriptions([...tempPrescriptions, newP]);
        setPrescriptionForm({
            name: '',
            start: '09:00',
            freq: '8',
            duration: '5'
        });
    };

    const handleRemovePrescription = (id: number) => {
        setTempPrescriptions(tempPrescriptions.filter(p => p.id !== id));
    };

    const downloadQR = () => {
        const svg = document.getElementById("qr-code-svg");
        if (svg) {
            const svgData = new XMLSerializer().serializeToString(svg);
            const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `QR_${selectedQrPet?.name || 'Pet'}.svg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleStartTransfer = (pet: any) => {
        setTransferPet(pet);
        setShowTransferModal(true);
    };

    const handleSubmitValidation = async () => {
        if (!selectedPetDetail || !validationReqData.pedigreeReg) return;
        setIsSubmittingValidation(true);
        try {
            await api.breeder.submitValidationRequest({
                pet_id: selectedPetDetail.id,
                pedigree_reg: validationReqData.pedigreeReg,
                document: validationReqData.document
            });
            setShowValidationRequestModal(false);
            setValidationReqData({ pedigreeReg: '', document: null });
            await Promise.all([loadInventory(), loadKennelDocs()]);
            setSuccessModal({
                isOpen: true,
                title: 'Solicitud Enviada',
                message: 'Tu solicitud ha sido recibida correctamente. El equipo de <strong>Amigo Peludo</strong> revisará la documentación y el ejemplar recibirá su sello cuando sea aprobada.'
            });
        } catch (e: any) {
            console.error(e);
            alert('No se pudo enviar la solicitud: ' + (e.message || 'intenta nuevamente'));
        } finally {
            setIsSubmittingValidation(false);
        }
    };

    const [showTokenModal, setShowTokenModal] = useState(false);
    const [generatedToken, setGeneratedToken] = useState('');

    const [tokenEmailSent, setTokenEmailSent] = useState(true);

    // Antes, si el servidor fallaba igual se mostraba un código inventado en el navegador (y no
    // servía para nada) y además se mandaba un segundo correo. Ahora solo se muestra un código
    // real, y el correo con el enlace de aceptación lo envía el servidor al crear el traspaso.
    const confirmTransfer = async () => {
        if (!transferEmail.trim() || !transferPet) return;
        setTransferBusy(true);
        setTransferError('');
        try {
            const res = await api.breeder.createTransfer({ petId: transferPet.id, toEmail: transferEmail.trim() }, asUser);
            setGeneratedToken(res.token);
            setTokenEmailSent(!!res.emailSent);
            setShowTransferModal(false);
            setShowTokenModal(true);
            await Promise.all([loadTransfers(), loadInventory()]);
        } catch (e: any) {
            setTransferError(e.message || 'No se pudo iniciar el traspaso');
        } finally {
            setTransferBusy(false);
        }
    };

    const cancelTransfer = async (t: any) => {
        if (!confirm(`¿Cancelar el traspaso de ${t.petName}?`)) return;
        try {
            await api.breeder.cancelTransfer(t.id, asUser);
            await Promise.all([loadTransfers(), loadInventory()]);
        } catch (e: any) {
            alert('No se pudo cancelar: ' + (e.message || 'intenta nuevamente'));
        }
    };

    const openPetDetail = (pet: any) => {
        setSelectedPetDetail(pet);
        setActiveDetailTab('growth');
        loadGrowthLogs(pet.id);
        loadPetDocs(pet.id);
        loadPetGallery(pet.id);
        setActiveTab('pet_detail');

    };

    const isElite = planInfo?.plan === 'elite';
    const publicUrl = `${window.location.origin}/criadero/${breederProfile?.slug || user?.id}`;
    const [linkCopied, setLinkCopied] = useState(false);
    const copyPublicUrl = () => {
        navigator.clipboard?.writeText(publicUrl).then(() => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 1800); }).catch(() => { });
    };
    // Mostrar/ocultar un ejemplar en la página pública, y su precio (desde el plan Pro).
    const savePublicData = async (pet: any, patch: { publicVisible?: boolean; price?: string }) => {
        try {
            await api.breeder.setPetData({ petId: pet.id, ...patch }, asUser);
            await loadInventory();
        } catch (e: any) {
            if (e.code === 'PLAN_PRO') askPro(e.message); else alert('No se pudo guardar: ' + (e.message || 'intenta nuevamente'));
        }
    };
    const [priceDraft, setPriceDraft] = useState('');

    const pendingTransfers = transfers.filter(t => t.status === 'pending').length;
    const NAV_ITEMS: { id: string; label: string; short: string; icon: any }[] = [
        { id: 'dashboard', label: 'Resumen', short: 'Resumen', icon: LayoutDashboard },
        { id: 'manada', label: 'Manada', short: 'Manada', icon: Dog },
        ...(activeTab === 'pet_detail' ? [{ id: 'pet_detail', label: selectedPetDetail?.name || 'Mascota', short: selectedPetDetail?.name || 'Ficha', icon: Heart }] : []),
        { id: 'litters', label: 'Camadas y celos', short: 'Camadas', icon: Baby },
        { id: 'pedigree', label: 'Genealogía', short: 'Linaje', icon: Activity },
        { id: 'transfers', label: 'Traspasos', short: 'Traspasos', icon: Share2 },
        { id: 'waitlist', label: 'Lista de espera', short: 'Espera', icon: ListChecks },
        { id: 'contracts', label: 'Contratos', short: 'Contratos', icon: FileSignature },
        { id: 'docs', label: 'Documentación', short: 'Docs', icon: FileText },
        { id: 'settings', label: 'Configuración', short: 'Ajustes', icon: Settings },
    ];

    const changePetStatus = async (pet: any, status: string) => {
        try {
            await api.breeder.setPetData({ petId: pet.id, status }, asUser);
            await loadInventory();
        } catch (e: any) {
            alert('No se pudo cambiar el estado: ' + (e.message || 'intenta nuevamente'));
        }
    };

    return (
        <div className="max-w-full mx-auto px-4 sm:px-6 pb-20 pt-6 animate-in fade-in duration-700 relative">

            {/* Token Success Modal */}
            {showTokenModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-emerald-950/90 backdrop-blur-md p-4">
                    <div className="bg-white rounded-[3rem] p-10 max-w-sm w-full shadow-2xl text-center border-4 border-emerald-400">
                        <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle size={40} />
                        </div>
                        <h3 className="text-2xl font-black text-slate-800 uppercase italic mb-2">Traspaso Iniciado</h3>
                        <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-6">Comparte este código con el dueño</p>

                        <div className="bg-slate-900 text-[#FF6B00] p-5 rounded-[7px] mb-4 font-mono text-2xl font-black tracking-widest shadow-inner border-2 border-slate-700 break-all">
                            {generatedToken}
                        </div>
                        <button
                            onClick={() => navigator.clipboard?.writeText(generatedToken).catch(() => { })}
                            className="mb-4 inline-flex items-center gap-2 text-[10px] font-black uppercase text-slate-500 hover:text-slate-800 transition-colors"
                        >
                            <Copy size={14} /> Copiar código
                        </button>
                        <p className="text-[11px] font-medium text-slate-500 leading-relaxed mb-6">
                            {tokenEmailSent
                                ? <>Enviamos un correo a <strong>{transferEmail}</strong> con el enlace para aceptar. También puede ingresar el código en <em>Mis mascotas → ¿Tienes un código de traspaso?</em>.</>
                                : <>No pudimos enviar el correo automáticamente: entrégale este código a <strong>{transferEmail}</strong>. Lo ingresa en <em>Mis mascotas → ¿Tienes un código de traspaso?</em>.</>}
                        </p>

                        <button
                            onClick={() => { setShowTokenModal(false); setTransferPet(null); setTransferEmail(''); }}
                            className="w-full bg-slate-900 text-white py-4 rounded-[7px] font-black uppercase text-xs"
                        >
                            Listo, Cerrar
                        </button>
                    </div>
                </div>
            )}

            {/* Pedigree Modal — árbol completo (abuelos/padres y, hacia abajo, hijos/nietos) del ejemplar seleccionado */}
            {showPedigreeModal && selectedPetDetail && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 overflow-y-auto" onClick={() => setShowPedigreeModal(false)}>
                    <div className="w-full max-w-3xl my-8" onClick={e => e.stopPropagation()}>
                        <PedigreeView selectedPet={selectedPetDetail} inventory={inventory} onSelectPet={() => setShowPedigreeModal(false)} />
                    </div>
                </div>
            )}

            {/* New Pet Modal */}
            {showNewPetModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-md p-4" onClick={() => setShowNewPetModal(false)}>
                    <div className="bg-white rounded-[2.5rem] p-8 max-w-lg w-full shadow-2xl animate-in fade-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-8">
                            <h2 className="text-2xl font-black text-slate-800 italic uppercase">Nuevo Miembro</h2>
                            <button onClick={() => setShowNewPetModal(false)} className="bg-slate-100 p-2 rounded-[7px] text-slate-400 hover:text-slate-600 transition-colors"><X size={20} /></button>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="col-span-2">
                                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Nombre del Ejemplar</label>
                                <input
                                    className="w-full bg-slate-50 border border-slate-200 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold"
                                    value={newPetData.name}
                                    onChange={e => setNewPetData({ ...newPetData, name: e.target.value })}
                                    placeholder="Ej: Zeus"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Raza</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold appearance-none"
                                    value={newPetData.breed}
                                    onChange={e => setNewPetData({ ...newPetData, breed: e.target.value })}
                                >
                                    <option value="">Seleccionar Raza</option>
                                    {breeds.map(breed => (
                                        <option key={breed} value={breed}>{breed}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Fecha de Nacimiento</label>
                                <input
                                    type="date"
                                    className="w-full bg-slate-50 border border-slate-200 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold"
                                    value={newPetData.birthDate}
                                    onChange={e => setNewPetData({ ...newPetData, birthDate: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Género</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold appearance-none"
                                    value={newPetData.gender}
                                    onChange={e => setNewPetData({ ...newPetData, gender: e.target.value })}
                                >
                                    <option value="Macho">Macho</option>
                                    <option value="Hembra">Hembra</option>
                                </select>
                            </div>

                            <div className="col-span-2 p-4 bg-orange-50 rounded-[7px] border border-orange-100">
                                <p className="text-[9px] font-black text-[#FF6B00] uppercase tracking-widest mb-4 flex items-center gap-2"><Info size={12} /> Datos de Nacimiento (Mandatorios)</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Peso Inicial (Kg)</label>
                                        <input
                                            type="number" step="0.001"
                                            className="w-full bg-white border border-slate-200 p-3 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold text-xs"
                                            value={newPetData.birthWeight}
                                            onChange={e => setNewPetData({ ...newPetData, birthWeight: e.target.value })}
                                            placeholder="Ej: 0.450"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Talla Inicial (cm)</label>
                                        <input
                                            type="number" step="0.1"
                                            className="w-full bg-white border border-slate-200 p-3 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold text-xs"
                                            value={newPetData.birthHeight}
                                            onChange={e => setNewPetData({ ...newPetData, birthHeight: e.target.value })}
                                            placeholder="Ej: 12.5"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Estado</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold appearance-none"
                                    value={newPetData.status}
                                    onChange={e => setNewPetData({ ...newPetData, status: e.target.value })}
                                >
                                    <option value="disponible">Disponible</option>
                                    <option value="reservado">Reservado</option>
                                    <option value="criadero">Ejemplar Criadero</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Microchip (Opcional)</label>
                                <input
                                    className="w-full bg-slate-50 border border-slate-200 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold"
                                    value={newPetData.microchip}
                                    onChange={e => setNewPetData({ ...newPetData, microchip: e.target.value })}
                                />
                            </div>
                            {litters.length > 0 && (
                                <div className="col-span-2">
                                    <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Camada (Opcional)</label>
                                    <select
                                        className="w-full bg-slate-50 border border-slate-200 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold appearance-none"
                                        value={newPetData.litterId}
                                        onChange={e => {
                                            const l = litters.find(x => String(x.id) === e.target.value);
                                            // Elegir una camada completa padres y fecha de nacimiento.
                                            setNewPetData(l ? {
                                                ...newPetData, litterId: e.target.value, birthDate: l.birthDate || newPetData.birthDate,
                                                fatherId: l.fatherId || (l.fatherName ? 'external' : newPetData.fatherId),
                                                motherId: l.motherId || (l.motherName ? 'external' : newPetData.motherId),
                                            } : { ...newPetData, litterId: '' });
                                        }}
                                    >
                                        <option value="">Sin camada</option>
                                        {litters.map(l => <option key={l.id} value={l.id}>{l.name || 'Camada'} · {l.birthDate}</option>)}
                                    </select>
                                </div>
                            )}

                            <div className="col-span-2 grid grid-cols-2 gap-4 p-4 bg-orange-50/60 rounded-[7px] border border-orange-100">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-sky-500 tracking-widest mb-2">Padre (Semental)</label>
                                    <button
                                        onClick={() => setParentSelectionModal({ isOpen: true, type: 'padre' })}
                                        className="w-full bg-white border border-sky-200 p-3 rounded-[7px] outline-none focus:ring-2 focus:ring-sky-300 transition-all font-bold text-xs text-left text-slate-700 flex justify-between items-center"
                                    >
                                        <span className={newPetData.fatherId ? 'text-sky-600' : 'text-slate-400'}>
                                            {newPetData.fatherId
                                                ? (newPetData.fatherId === 'external' ? 'Padre Externo' : fathers.find(f => f.id == newPetData.fatherId)?.name)
                                                : 'Seleccionar Padre'}
                                        </span>
                                        <Search size={14} className="text-sky-300" />
                                    </button>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-rose-400 tracking-widest mb-2">Madre (Progenitora)</label>
                                    <button
                                        onClick={() => setParentSelectionModal({ isOpen: true, type: 'madre' })}
                                        className="w-full bg-white border border-rose-200 p-3 rounded-[7px] outline-none focus:ring-2 focus:ring-rose-300 transition-all font-bold text-xs text-left text-slate-700 flex justify-between items-center"
                                    >
                                        <span className={newPetData.motherId ? 'text-rose-500' : 'text-slate-400'}>
                                            {newPetData.motherId
                                                ? (newPetData.motherId === 'external' ? 'Madre Externa' : mothers.find(m => m.id == newPetData.motherId)?.name)
                                                : 'Seleccionar Madre'}
                                        </span>
                                        <Search size={14} className="text-rose-300" />
                                    </button>
                                </div>
                                <div className="col-span-2 p-6 bg-gradient-to-br from-amber-50 via-white to-orange-50 rounded-[2.5rem] border-2 border-amber-200 relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 p-4 opacity-[0.12] group-hover:opacity-20 transition-opacity">
                                        <Award size={80} className="text-amber-400" />
                                    </div>
                                    <label className="block text-[10px] font-black uppercase text-amber-600 tracking-widest mb-4 flex items-center gap-2 relative z-10">
                                        <ShieldCheck size={14} /> Certificación de Pedigrí (Kennel Club)
                                    </label>
                                    <div className="space-y-4 relative z-10">
                                        <input
                                            className="w-full bg-white border-2 border-amber-200 p-4 rounded-[7px] outline-none focus:border-amber-500 font-mono text-amber-700 font-black placeholder:text-amber-300 placeholder:font-medium"
                                            value={newPetData.pedigreeReg}
                                            onChange={e => setNewPetData({ ...newPetData, pedigreeReg: e.target.value.toUpperCase() })}
                                            placeholder="EJ: KCC-123.456"
                                        />
                                        <p className="text-[8px] font-bold text-amber-700/70 uppercase">La validación de este número otorgará el sello de autenticidad al ejemplar.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleAddNewPet}
                            className="w-full bg-[#FF6B00] text-white py-5 rounded-[7px] font-black uppercase text-xs tracking-[0.2em] shadow-xl hover:bg-[#E65C00] active:scale-95 transition-all"
                        >
                            Confirmar Registro
                        </button>
                    </div>
                </div>
            )}

            {/* QR Modal */}
            {selectedQrPet && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setSelectedQrPet(null)}>
                    <div className="bg-white rounded-[2rem] p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                        <div className="text-center mb-6">
                            <h3 className="text-2xl font-black text-slate-800 uppercase italic">QR de Identidad</h3>
                            <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Para grabado láser de alta precisión</p>
                        </div>

                        <div className="bg-white p-4 rounded-[7px] border-4 border-slate-900 mx-auto w-fit mb-6 shadow-xl">
                            <QRCodeSVG
                                id="qr-code-svg"
                                value={`https://amigopeludo.cl/?view=found&id=${selectedQrPet.id}`}
                                size={256}
                                level="H"
                                includeMargin={true}
                            />
                        </div>

                        <div className="flex gap-4">
                            <button onClick={downloadQR} className="flex-1 bg-[#FF6B00] text-white py-4 rounded-[7px] font-black uppercase text-xs shadow-lg hover:scale-105 transition-transform">
                                Descargar SVG
                            </button>
                            <button onClick={() => setSelectedQrPet(null)} className="flex-1 bg-slate-100 text-slate-600 py-4 rounded-[7px] font-black uppercase text-xs hover:bg-slate-200 transition-colors">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transfer Modal */}
            {showTransferModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setShowTransferModal(false)}>
                    <div className="bg-white rounded-[2rem] p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                        <div className="text-center mb-6">
                            <Share2 size={48} className="text-[#FF6B00] mx-auto mb-4" />
                            <h3 className="text-2xl font-black text-slate-800 uppercase italic">Traspaso Digital</h3>
                            <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Transfiere la propiedad de {transferPet?.name}</p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Email del Nuevo Dueño</label>
                                <input
                                    type="email"
                                    value={transferEmail}
                                    onChange={e => setTransferEmail(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-[7px] px-4 py-3 font-medium outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/20 transition-all"
                                    placeholder="ejemplo@correo.com"
                                />
                                <p className="text-[10px] text-slate-400 font-medium mt-2 leading-relaxed">
                                    Debe ser el correo con el que el nuevo dueño usa Amigo Peludo: solo esa cuenta podrá aceptar el traspaso. El ejemplar queda "Reservado" hasta que acepte.
                                </p>
                                <button type="button" onClick={() => { setSelectedAdoptionPet(transferPet); setShowTransferModal(false); setShowAdoptionModal(true); }}
                                    className="mt-3 inline-flex items-center gap-2 text-[10px] font-black text-[#FF6B00] uppercase tracking-widest hover:underline">
                                    <Search size={12} /> ¿Ya es usuario? Buscarlo en Amigo Peludo
                                </button>
                            </div>
                            {transferError && <p className="text-xs font-bold text-rose-500 bg-rose-50 rounded-[7px] px-4 py-3">{transferError}</p>}

                            <button onClick={confirmTransfer} disabled={transferBusy || !transferEmail.trim()} className="w-full bg-[#FF6B00] text-white py-4 rounded-[7px] font-black uppercase text-xs shadow-lg hover:bg-[#E65C00] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                {transferBusy ? <><Loader2 size={16} className="animate-spin" /> Enviando…</> : 'Generar Token de Traspaso'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Adoption / User Search Modal */}
            {showAdoptionModal && (
                <div className="fixed inset-0 z-[130] flex items-center justify-center bg-rose-950/90 backdrop-blur-xl p-4" onClick={() => setShowAdoptionModal(false)}>
                    <div className="bg-white rounded-[3rem] p-6 sm:p-10 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
                        <div className="text-center mb-8 shrink-0">
                            <div className="w-20 h-20 bg-rose-100 text-rose-600 rounded-[7px] flex items-center justify-center mx-auto mb-6 rotate-6 shadow-lg">
                                <UserPlus size={40} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-800 uppercase italic">Buscar al nuevo dueño</h3>
                            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-2">Busca al usuario de Amigo Peludo que recibirá a {selectedAdoptionPet?.name}</p>
                        </div>

                        <div className="relative mb-6 shrink-0">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                            <input
                                type="text"
                                placeholder="Escribe nombre o email (mín 3 carac.)..."
                                className="w-full bg-slate-50 border-2 border-slate-100 pl-12 pr-4 py-4 rounded-[7px] outline-none focus:border-rose-500 font-bold transition-all"
                                value={userSearchTerm}
                                onChange={(e) => handleSearchUsers(e.target.value)}
                                autoFocus
                            />
                            {isSearchingUsers && (
                                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                    <Loader2 className="animate-spin text-rose-500" size={18} />
                                </div>
                            )}
                        </div>

                        <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scrollbar">
                            {foundUsers.map(u => (
                                <button
                                    key={u.id}
                                    onClick={() => handleStartAdoption(selectedAdoptionPet, u)}
                                    className="w-full p-4 rounded-[7px] bg-slate-50 border border-slate-100 hover:border-rose-500 hover:bg-rose-50 transition-all flex items-center gap-4 text-left group"
                                >
                                    <div className="w-12 h-12 rounded-[7px] overflow-hidden shadow-sm bg-white shrink-0">
                                        <img src={u.image_url || 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba'} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-black text-slate-800 uppercase italic truncate">{u.name}</p>
                                        <p className="text-[10px] font-bold text-slate-400 truncate tracking-tight">{u.email}</p>
                                    </div>
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-rose-600 text-white p-2 rounded-[7px] shadow-lg">
                                        <ArrowRight size={14} />
                                    </div>
                                </button>
                            ))}

                            {userSearchTerm.length >= 3 && foundUsers.length === 0 && !isSearchingUsers && (
                                <div className="text-center py-10">
                                    <Search size={32} className="mx-auto text-slate-200 mb-3" />
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-tight">No encontramos usuarios con ese nombre o email</p>
                                </div>
                            )}

                            {userSearchTerm.length < 3 && (
                                <div className="text-center py-10 opacity-30">
                                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest italic">Ingresa al menos 3 caracteres...</p>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => setShowAdoptionModal(false)}
                            className="w-full mt-6 py-4 bg-slate-100 text-slate-400 rounded-[7px] font-black uppercase text-xs hover:bg-slate-200 transition-all"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {/* Header Profile */}
            <div className={`p-8 sm:p-12 shadow-2xl relative overflow-hidden mb-8 border transition-all duration-500 ease-in-out ${breederProfile?.bannerUrl ? 'border-white/10' : 'border-orange-100'} ${isSidebarOpen ? 'lg:ml-80' : 'lg:ml-0'}`}>
                {breederProfile?.bannerUrl ? (
                    <>
                        <img src={breederProfile.bannerUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/55 to-slate-900/25" />
                    </>
                ) : (
                    <>
                        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-amber-50" />
                        <div className="absolute -top-16 -left-16 w-64 h-64 bg-orange-200/30 rounded-full blur-3xl" />
                        <div className="absolute -bottom-20 -right-10 w-72 h-72 bg-amber-200/30 rounded-full blur-3xl" />
                    </>
                )}
                <div className={`absolute top-0 right-0 p-12 ${breederProfile?.bannerUrl ? 'opacity-5' : 'opacity-[0.07]'}`}><Dog size={300} className={`rotate-12 ${breederProfile?.bannerUrl ? 'text-white' : 'text-orange-500'}`} /></div>
                <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                    <div className="w-32 h-32 rounded-[7px] bg-white p-2 shadow-xl shrink-0 rotate-3 flex items-center justify-center">
                        {(breederProfile?.logoUrl || user?.imageUrl)
                            ? <img src={breederProfile?.logoUrl || user?.imageUrl} className="w-full h-full object-cover rounded-[7px]" />
                            : <Dog size={48} className="text-slate-300" />}
                    </div>
                    <div className={`text-center md:text-left ${breederProfile?.bannerUrl ? 'text-white' : 'text-slate-800'}`}>
                        {breederProfile?.verified ? (
                            <div className="inline-flex items-center gap-2 bg-[#FF6B00] text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-4 shadow-lg border border-white/20">
                                <ShieldCheck size={14} /> Criadero Verificado
                            </div>
                        ) : (
                            <button onClick={() => setActiveTab('docs')} className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-4 border transition-colors ${breederProfile?.bannerUrl ? 'bg-white/10 border-white/20 hover:bg-white/20 text-white' : 'bg-orange-50 border-orange-200 hover:bg-orange-100 text-orange-700'}`}>
                                <Clock size={14} /> Verificación pendiente — solicita el pedigrí de un ejemplar
                            </button>
                        )}
                        {planInfo && (
                            <button onClick={() => setUpgradeReason('')} className={`ml-0 md:ml-2 mb-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border transition-colors ${planInfo.plan !== 'free'
                                ? (breederProfile?.bannerUrl ? 'bg-amber-400/20 text-amber-300 border-amber-300/30 hover:bg-amber-400/30' : 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200')
                                : (breederProfile?.bannerUrl ? 'bg-white/10 text-white/80 border-white/20 hover:bg-white/20' : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200')}`}>
                                {planInfo.plan !== 'free'
                                    ? <>{planInfo.plan === 'elite' ? 'Criador Élite' : 'Criador Pro'}{proDaysLeft(planInfo) !== null ? ` · ${proDaysLeft(planInfo)} días` : ''}</>
                                    : <>Plan Gratis · {planInfo.usage.pets}/{planInfo.limits.pets} ejemplares · Mejorar</>}
                            </button>
                        )}
                        <h1 className="text-4xl sm:text-6xl font-black italic uppercase tracking-tighter leading-none mb-2">
                            {breederProfile?.kennelName || "Tu Criadero"}
                        </h1>
                        <p className={`font-bold uppercase tracking-[0.2em] text-xs ${breederProfile?.bannerUrl ? 'text-white/70' : 'text-slate-400'}`}>
                            {breederProfile?.location || 'Ubicación por definir'}{breederProfile?.specialty ? ` • Esp: ${breederProfile.specialty}` : ''}
                        </p>
                    </div>
                </div>
            </div>

            {/* Parent Selection Modal (Shared) */}
            <ParentSelectionModal
                isOpen={parentSelectionModal.isOpen}
                type={parentSelectionModal.type as 'padre' | 'madre'}
                title={parentSelectionModal.type === 'padre' ? 'Padre' : 'Madre'}
                candidates={parentSelectionModal.type === 'padre' ? fathers : mothers}
                onClose={() => setParentSelectionModal({ ...parentSelectionModal, isOpen: false })}
                onSelect={(id) => {
                    if (parentSelectionModal.type === 'padre') {
                        setNewPetData({ ...newPetData, fatherId: id });
                    } else {
                        setNewPetData({ ...newPetData, motherId: id });
                    }
                }}
            />

            <div className="flex flex-col lg:flex-row gap-8 relative">
                {/* Sidebar Navigation */}
                <aside
                    className={`hidden lg:flex max-w-xs space-y-4 lg:fixed lg:inset-y-0 lg:left-0 z-50 bg-[#F8F9FA] transition-all duration-500 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                        }`}
                >
                    <div className="p-4 sm:p-6 lg:p-8 h-full flex flex-col gap-4 overflow-y-auto custom-scrollbar">
                        <nav className="bg-white rounded-[7px] p-4 shadow-xl border border-slate-100 flex flex-col gap-2">
                            {NAV_ITEMS.map(item => (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveTab(item.id as any)}
                                    className={`w-full p-4 rounded-[7px] flex items-center gap-4 transition-all group ${activeTab === item.id ? 'bg-[#FF6B00] text-white shadow-lg' : 'hover:bg-slate-50 text-slate-600'}`}
                                >
                                    <item.icon size={20} className={activeTab === item.id ? 'text-white' : 'text-slate-400 group-hover:text-[#FF6B00]'} />
                                    <span className="font-bold text-xs uppercase tracking-widest">{item.label}</span>
                                    {activeTab === item.id && <ArrowRight size={16} className="ml-auto" />}
                                </button>
                            ))}

                            <div className="h-px bg-slate-100 my-2 mx-4"></div>

                            <button
                                onClick={() => window.open(publicUrl, '_blank')}
                                className="w-full p-4 rounded-[7px] flex items-center gap-4 text-orange-600 hover:bg-orange-50 transition-all group border-2 border-dashed border-orange-100"
                            >
                                <ExternalLink size={20} className="text-orange-400 group-hover:text-orange-600" />
                                <span className="font-bold text-xs uppercase tracking-widest">Página Pública</span>
                            </button>

                            <button
                                onClick={() => onExit(View.USER_HUB)}
                                className="w-full mt-2 p-4 rounded-[7px] flex items-center gap-4 text-rose-500 hover:bg-rose-50 transition-all group border border-dashed border-rose-200"
                            >
                                <X size={20} className="text-rose-400 group-hover:text-rose-600" />
                                <span className="font-bold text-xs uppercase tracking-widest">Salir del Panel</span>
                            </button>
                        </nav>

                    </div>
                </aside>

                {/* Navegación móvil/tablet: la barra lateral solo existe en escritorio (lg), sin esto no había cómo cambiar de sección. */}
                <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 px-2 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] flex justify-around overflow-x-auto">
                    {NAV_ITEMS.filter(i => i.id !== 'pet_detail').map(item => (
                        <button key={item.id} onClick={() => setActiveTab(item.id as any)}
                            className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-[7px] min-w-[3.1rem] transition-colors ${activeTab === item.id ? 'text-[#FF6B00] bg-orange-50' : 'text-slate-400'}`}>
                            <item.icon size={20} />
                            <span className="text-[8px] font-black uppercase tracking-wide">{item.short}</span>
                        </button>
                    ))}
                    <button onClick={() => onExit(View.USER_HUB)} className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-[7px] min-w-[3.1rem] text-rose-400">
                        <LogOut size={20} />
                        <span className="text-[8px] font-black uppercase tracking-wide">Salir</span>
                    </button>
                </nav>

                {/* Main Content Area */}
                <main className={`w-full pb-32 lg:pb-0 transition-all duration-500 ease-in-out ${isSidebarOpen ? 'lg:ml-80 lg:w-[calc(100%-20rem)]' : 'lg:ml-0 lg:w-full'}`}>
                    {loadError && (
                        <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-[7px] flex items-center justify-between gap-3">
                            <p className="text-xs font-bold text-rose-600">{loadError}</p>
                            <button onClick={() => { setLoadError(''); loadBreederProfile(); reloadAll(); }} className="text-[10px] font-black text-rose-600 uppercase underline shrink-0">Reintentar</button>
                        </div>
                    )}
                    {/* Pet Detail Modal (Now rendered as Main Content if activeTab is 'pet_detail') */}
                    {activeTab === 'pet_detail' && selectedPetDetail && (
                        <div className="bg-white rounded-[3rem] w-full shadow-2xl animate-in slide-in-from-right-8 duration-500 overflow-hidden border border-slate-100 mb-8">
                            <div className="flex flex-col md:flex-row h-full min-h-[600px]">
                                {/* Sidebar Detail */}
                                <div className="md:w-1/3 bg-slate-50 p-8 border-r border-slate-100 flex flex-col items-center">
                                    <button onClick={() => { setActiveTab('manada'); }} className="self-start mb-4 p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400 flex items-center gap-2"><ArrowRight size={16} className="rotate-180" /> <span className="text-[10px] font-bold uppercase">Volver</span></button>
                                    <div className="relative group w-48 h-48 rounded-[3rem] border-8 border-white shadow-xl overflow-hidden mb-6">
                                        {selectedPetDetail.imageUrl
                                            ? <img src={selectedPetDetail.imageUrl} className="w-full h-full object-cover" />
                                            : <div className="w-full h-full bg-slate-100 flex items-center justify-center"><Dog size={64} className="text-slate-300" /></div>}
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <label className="cursor-pointer p-4 bg-white/20 backdrop-blur-md rounded-[7px] text-white hover:bg-white/40 transition-all">
                                                <Camera size={24} />
                                                <input type="file" accept="image/*" className="hidden" onChange={handleUpdateProfilePicture} />
                                            </label>
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-black text-slate-800 uppercase italic mb-1">{selectedPetDetail.name}</h3>
                                    <div className="flex items-center gap-2 mb-6">
                                        <span className="bg-orange-50 text-orange-600 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">{selectedPetDetail.status}</span>
                                        <button
                                            onClick={() => setSelectedQrPet(selectedPetDetail)}
                                            className="w-7 h-7 bg-slate-900 text-white rounded-full flex items-center justify-center hover:bg-black transition-colors shadow-lg hover:scale-110 transform duration-200"
                                            title="Ver Código QR"
                                        >
                                            <QrCode size={14} />
                                        </button>
                                        </div>

                                    <div className="w-full space-y-4">
                                        {/* El sello solo aparece cuando el equipo aprobó el pedigrí; un número declarado no basta. */}
                                        {selectedPetDetail.pedigreeVerified ? (
                                            <div className="bg-emerald-50 p-4 rounded-[7px] border-2 border-emerald-200 shadow-lg shadow-emerald-500/10 flex items-center gap-4">
                                                <div className="w-10 h-10 bg-emerald-500 rounded-[7px] flex items-center justify-center text-white shadow-lg">
                                                    <Award size={20} />
                                                </div>
                                                <div>
                                                    <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest leading-none mb-1">Pedigrí Verificado</p>
                                                    <p className="text-xs font-black text-slate-800 font-mono tracking-tighter">{selectedPetDetail.pedigreeReg}</p>
                                                </div>
                                            </div>
                                        ) : selectedPetDetail.pedigreeStatus === 'pending' ? (
                                            <div className="bg-amber-50 p-4 rounded-[7px] border-2 border-amber-200 flex items-center gap-4">
                                                <div className="w-10 h-10 bg-amber-400 rounded-[7px] flex items-center justify-center text-white"><Clock size={20} /></div>
                                                <div>
                                                    <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest leading-none mb-1">Validación en revisión</p>
                                                    <p className="text-xs font-black text-slate-800 font-mono tracking-tighter">{selectedPetDetail.pedigreeReg}</p>
                                                </div>
                                            </div>
                                        ) : selectedPetDetail.owned !== false && (
                                            <div className="space-y-2">
                                                {selectedPetDetail.pedigreeStatus === 'rejected' && (
                                                    <p className="text-[10px] font-bold text-rose-500 bg-rose-50 rounded-[7px] px-3 py-2">
                                                        La última solicitud fue rechazada{selectedPetDetail.pedigreeNotes ? `: ${selectedPetDetail.pedigreeNotes}` : '.'}
                                                    </p>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        setValidationReqData({ document: null, pedigreeReg: selectedPetDetail.pedigreeReg || '' });
                                                        setShowValidationRequestModal(true);
                                                    }}
                                                    className="w-full bg-amber-500 text-white p-4 rounded-[7px] font-black uppercase text-[10px] tracking-widest shadow-lg shadow-amber-500/20 hover:scale-105 transition-all flex items-center justify-center gap-2"
                                                >
                                                    <ShieldCheck size={16} /> {selectedPetDetail.pedigreeStatus === 'rejected' ? 'Volver a solicitar' : 'Solicitar Validación'}
                                                </button>
                                            </div>
                                        )}
                                        <div className="bg-gradient-to-br from-orange-500 to-[#E65C00] p-6 rounded-[2rem] shadow-xl shadow-orange-600/20 border-b-4 border-orange-700 relative overflow-hidden group">
                                            <div className="absolute -right-4 -bottom-4 opacity-10 group-hover:scale-110 transition-transform">
                                                <Award size={100} className="text-white" />
                                            </div>
                                            <p className="text-[10px] font-black text-orange-100 uppercase tracking-[0.2em] mb-1 relative z-10">Raza del Ejemplar</p>
                                            <p className="text-2xl font-black text-white italic uppercase tracking-tighter relative z-10 leading-tight">
                                                {selectedPetDetail.breed || 'Sin definir'}
                                            </p>
                                        </div>
                                        {selectedPetDetail.owned !== false && selectedPetDetail.status !== 'entregado' && (
                                            <div className="bg-white p-4 rounded-[7px] shadow-sm border border-slate-100 space-y-3">
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Página pública</p>
                                                <button onClick={() => savePublicData(selectedPetDetail, { publicVisible: !selectedPetDetail.publicVisible })}
                                                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-[7px] text-[11px] font-black uppercase tracking-wide transition-colors ${selectedPetDetail.publicVisible ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                                                    <span className="flex items-center gap-2">{selectedPetDetail.publicVisible ? <Eye size={14} /> : <EyeOff size={14} />} {selectedPetDetail.publicVisible ? 'Visible en tu página' : 'Oculto'}</span>
                                                    <span className="text-[9px]">Cambiar</span>
                                                </button>
                                                <div className="flex gap-2">
                                                    <input inputMode="numeric" placeholder={selectedPetDetail.price ? `Precio: $${Number(selectedPetDetail.price).toLocaleString('es-CL')}` : 'Precio en CLP (opcional)'} value={priceDraft}
                                                        onChange={e => setPriceDraft(e.target.value.replace(/\D/g, ''))}
                                                        className="flex-1 min-w-0 bg-slate-50 border border-slate-200 px-3 py-2.5 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold text-xs" />
                                                    <button onClick={async () => { await savePublicData(selectedPetDetail, { price: priceDraft }); setPriceDraft(''); }}
                                                        className="px-3 rounded-[7px] bg-[#FF6B00] hover:bg-[#E65C00] text-white text-[10px] font-black uppercase transition-colors">{priceDraft === '' && selectedPetDetail.price ? 'Quitar' : 'Guardar'}</button>
                                                </div>
                                                <p className="text-[9px] text-slate-400 font-medium leading-relaxed">El precio en la página pública es del plan Pro en adelante.</p>
                                            </div>
                                        )}
                                        {selectedPetDetail.owned !== false && selectedPetDetail.status !== 'entregado' && (
                                            <div className="bg-white p-4 rounded-[7px] shadow-sm border border-slate-100">
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Estado del ejemplar</p>
                                                <select
                                                    className="w-full bg-slate-50 border border-slate-200 px-3 py-2.5 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold text-xs disabled:opacity-60"
                                                    value={selectedPetDetail.status}
                                                    disabled={selectedPetDetail.status === 'reservado' && transfers.some(t => t.status === 'pending' && String(t.petId) === String(selectedPetDetail.id))}
                                                    onChange={e => changePetStatus(selectedPetDetail, e.target.value)}
                                                >
                                                    <option value="criadero">Reproductor (no está a la venta)</option>
                                                    <option value="disponible">Disponible</option>
                                                    <option value="reservado">Reservado</option>
                                                </select>
                                            </div>
                                        )}
                                        <button onClick={() => setShowPedigreeModal(true)} className="w-full text-left bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 hover:border-orange-200 hover:shadow-md transition-all group">
                                            <div className="flex items-center justify-between mb-4">
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Linaje</p>
                                                <span className="text-[9px] font-black text-orange-500 uppercase tracking-widest flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Ver árbol completo <ArrowRight size={11} /></span>
                                            </div>
                                            <div className="flex gap-6 justify-center">
                                                {/* Father */}
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className="w-14 h-14 rounded-full border-2 border-sky-100 p-0.5 shadow-sm overflow-hidden bg-slate-50 flex items-center justify-center">
                                                        {inventory.find(i => i.id == selectedPetDetail.fatherId)?.imageUrl
                                                            ? <img src={inventory.find(i => i.id == selectedPetDetail.fatherId)?.imageUrl} className="w-full h-full object-cover rounded-full" />
                                                            : <Dog size={22} className="text-slate-300" />}
                                                    </div>
                                                    <p className="text-[10px] font-black text-sky-600 uppercase italic flex items-center gap-1">
                                                        <ShieldCheck size={10} /> {inventory.find(i => i.id == selectedPetDetail.fatherId)?.name || 'Externo'}
                                                    </p>
                                                </div>
                                                {/* Mother */}
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className="w-14 h-14 rounded-full border-2 border-rose-100 p-0.5 shadow-sm overflow-hidden bg-slate-50 flex items-center justify-center">
                                                        {inventory.find(i => i.id == selectedPetDetail.motherId)?.imageUrl
                                                            ? <img src={inventory.find(i => i.id == selectedPetDetail.motherId)?.imageUrl} className="w-full h-full object-cover rounded-full" />
                                                            : <Dog size={22} className="text-slate-300" />}
                                                    </div>
                                                    <p className="text-[10px] font-black text-rose-500 uppercase italic flex items-center gap-1">
                                                        <Heart size={10} className="fill-rose-500" /> {inventory.find(i => i.id == selectedPetDetail.motherId)?.name || 'Externa'}
                                                    </p>
                                                </div>
                                            </div>
                                        </button>
                                        <div className="bg-white p-4 rounded-[7px] shadow-sm border border-slate-100">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Chip</p>
                                            <p className="text-xs font-bold text-slate-700">{selectedPetDetail.microchip || 'No registrado'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Main Detail Content */}
                                <div className="md:w-2/3 p-8 overflow-y-auto custom-scrollbar">
                                    {/* Tabs Navigation */}
                                    <div className="flex bg-slate-100 p-1.5 rounded-[7px] mb-8 w-fit mx-auto sticky top-0 z-10 shadow-sm border border-slate-200/50 backdrop-blur-md bg-slate-100/90">
                                        <button
                                            onClick={() => setActiveDetailTab('growth')}
                                            className={`px-6 py-3 rounded-[7px] text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeDetailTab === 'growth' ? 'bg-white text-emerald-600 shadow-md transform scale-105' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'}`}
                                        >
                                            <Activity size={14} /> Crecimiento
                                        </button>
                                        <button
                                            onClick={() => setActiveDetailTab('health')}
                                            className={`px-6 py-3 rounded-[7px] text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeDetailTab === 'health' ? 'bg-white text-sky-500 shadow-md transform scale-105' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'}`}
                                        >
                                            <ShieldCheck size={14} /> Carnet
                                        </button>
                                        <button
                                            onClick={() => setActiveDetailTab('history')}
                                            className={`px-6 py-3 rounded-[7px] text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeDetailTab === 'history' ? 'bg-white text-violet-500 shadow-md transform scale-105' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'}`}
                                        >
                                            <FileText size={14} /> Historial
                                        </button>
                                        <button
                                            onClick={() => setActiveDetailTab('galeria')}
                                            className={`px-6 py-3 rounded-[7px] text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeDetailTab === 'galeria' ? 'bg-white text-orange-500 shadow-md transform scale-105' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'}`}
                                        >
                                            <Camera size={14} /> Galería
                                        </button>
                                        <button
                                            onClick={() => setActiveDetailTab('docs')}
                                            className={`px-6 py-3 rounded-[7px] text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeDetailTab === 'docs' ? 'bg-white text-rose-500 shadow-md transform scale-105' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'}`}
                                        >
                                            <Files size={14} /> Documentos
                                        </button>
                                    </div>

                                    {activeDetailTab === 'growth' && (
                                        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                                            <div className="flex items-center gap-4 mb-8">
                                                <div className="p-3 bg-emerald-100 text-emerald-600 rounded-[7px]"><Activity size={24} /></div>
                                                <div>
                                                    <h4 className="text-xl font-black text-slate-800 italic uppercase">Curva de Crecimiento</h4>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Seguimiento obligatorio de peso y talla</p>
                                                </div>
                                            </div>

                                            <div className="space-y-6">
                                                {/* Evolution Chart */}
                                                <GrowthChart logs={petGrowthLogs} />

                                                {/* Evolution List */}
                                                <div className="bg-slate-50 rounded-[7px] p-6 border border-slate-100">
                                                    <div className="grid grid-cols-4 text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4 px-4">
                                                        <span>Fecha</span>
                                                        <span>Peso (Kg)</span>
                                                        <span>Talla (cm)</span>
                                                        <span>Estado</span>
                                                    </div>
                                                    <div className="space-y-2">
                                                        {petGrowthLogs.map(log => (
                                                            <div key={log.id} className="grid grid-cols-4 bg-white p-4 rounded-[7px] shadow-sm items-center text-xs font-bold text-slate-700">
                                                                <span className="text-slate-400">{log.date}</span>
                                                                <span className="text-emerald-600">{log.weight} Kg</span>
                                                                <span className="text-blue-600">{log.height} cm</span>
                                                                <span className="text-[9px] bg-slate-100 px-2 py-1 rounded-[7px] text-center text-slate-500 uppercase font-bold">{log.note || 'Control'}</span>
                                                            </div>
                                                        ))}
                                                        {petGrowthLogs.length === 0 && (
                                                            <p className="text-center text-xs text-slate-400 py-4 italic uppercase font-bold tracking-widest">No hay registros aún</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Add New Log */}
                                                <div className="bg-orange-50 rounded-[7px] p-6 border border-orange-100">
                                                    <h5 className="text-[10px] font-black text-[#FF6B00] uppercase tracking-widest mb-4">Actualizar Registro Semanal</h5>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            placeholder="Nuevo Peso (Kg)"
                                                            className="bg-white p-3 rounded-[7px] border border-orange-200 outline-none focus:ring-2 focus:ring-orange-300 transition-all font-bold text-xs"
                                                            value={growthLogForm.weight}
                                                            onChange={e => setGrowthLogForm({ ...growthLogForm, weight: e.target.value.replace(/[^0-9.]/g, '') })}
                                                        />
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            placeholder="Nueva Talla (cm)"
                                                            className="bg-white p-3 rounded-[7px] border border-orange-200 outline-none focus:ring-2 focus:ring-orange-300 transition-all font-bold text-xs"
                                                            value={growthLogForm.height}
                                                            onChange={e => setGrowthLogForm({ ...growthLogForm, height: e.target.value.replace(/[^0-9.]/g, '') })}
                                                        />
                                                        <button
                                                            onClick={handleSaveGrowthLog}
                                                            className="col-span-2 bg-[#FF6B00] text-white py-3 rounded-[7px] font-black uppercase text-[10px] shadow-lg hover:bg-[#E65C00] transition-all hover:scale-[1.02]"
                                                        >
                                                            Guardar Evolución
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {activeDetailTab === 'health' && (
                                        <BreederPetHealth
                                            pet={selectedPetDetail}
                                            type="vaccines"
                                            readOnly={selectedPetDetail.owned === false}
                                            onAddRecord={() => {
                                                setHealthFormData({ date: todayLocal() });
                                                setShowVaccineModal(true);
                                            }}
                                        />
                                    )}

                                    {activeDetailTab === 'history' && (
                                        <BreederPetHealth
                                            pet={selectedPetDetail}
                                            type="consultations"
                                            readOnly={selectedPetDetail.owned === false}
                                            onAddRecord={() => {
                                                setEditingConsultId(null);
                                                setTempPrescriptions([]);
                                                setHealthFormData({ date: todayLocal() });
                                                setShowConsultationModal(true);
                                            }}
                                            onEditRecord={handleEditConsultation}
                                            onDeleteRecord={handleDeleteConsultation}
                                        />
                                    )}

                                    {activeDetailTab === 'docs' && (
                                        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                                            <div className="flex justify-between items-center mb-8">
                                                <div>
                                                    <h4 className="text-xl font-black text-slate-800 italic uppercase">Expediente Digital</h4>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Documentación fundamental del ejemplar</p>
                                                </div>
                                                <button
                                                    onClick={() => setShowAddDocModal(true)}
                                                    className="bg-rose-600 text-white px-6 py-3 rounded-[7px] font-black uppercase text-[10px] flex items-center gap-2 hover:bg-rose-700 hover:scale-105 transition-all shadow-lg"
                                                >
                                                    <Plus size={16} /> Agregar
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-1 gap-4">
                                                {petDocs.map(doc => (
                                                    <div key={doc.id} className="p-6 bg-slate-50 rounded-[7px] border border-slate-100 flex items-center gap-4 group hover:bg-white hover:shadow-xl transition-all">
                                                        <div className="p-4 bg-white rounded-[7px] text-slate-400 group-hover:text-rose-500 transition-colors shadow-sm">
                                                            <FileText size={32} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-[9px] font-black text-rose-500 uppercase tracking-widest mb-1">{doc.document_type}</p>
                                                            <p className="text-xs font-bold text-slate-800">{doc.title}</p>
                                                            <p className="text-[10px] text-slate-400 font-medium">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                                                        </div>
                                                        <a
                                                            href={doc.file_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-3 bg-white text-slate-400 hover:text-rose-600 rounded-[7px] shadow-sm transition-colors"
                                                        >
                                                            <ExternalLink size={20} />
                                                        </a>
                                                        <button
                                                            onClick={async () => {
                                                                if (confirm('¿Eliminar documento?')) {
                                                                    await api.deletePetDocument(doc.id);
                                                                    loadPetDocs(selectedPetDetail.id);
                                                                }
                                                            }}
                                                            className="p-3 text-slate-300 hover:text-rose-500 transition-colors"
                                                        >
                                                            <X size={20} />
                                                        </button>
                                                    </div>
                                                ))}
                                                {petDocs.length === 0 && (
                                                    <div className="text-center py-12 bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-200">
                                                        <Files size={48} className="text-slate-200 mx-auto mb-4" />
                                                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest italic">No hay documentos registrados</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {activeDetailTab === 'galeria' && (
                                        <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
                                            <div className="flex justify-between items-center mb-8">
                                                <div>
                                                    <h4 className="text-xl font-black text-slate-800 italic uppercase">Galería de Imágenes</h4>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Máximo {photoMax} fotografías por ejemplar en tu plan</p>
                                                </div>
                                                <div className="relative overflow-hidden group">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleUploadGalleryImage}
                                                        disabled={loading || (petGallery || []).length >= photoMax}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-10"
                                                    />
                                                    <button
                                                        disabled={loading || (petGallery || []).length >= photoMax}
                                                        className="bg-emerald-600 text-white px-6 py-3 rounded-[7px] font-black uppercase text-[10px] flex items-center gap-2 hover:bg-emerald-700 transition-colors shadow-lg disabled:opacity-50"
                                                    >
                                                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                                                        {loading ? 'Subiendo...' : 'Agregar Foto'}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                                                {(petGallery || []).map((img, i) => (
                                                    <div key={img.id || i} className="aspect-square relative group rounded-[7px] overflow-hidden shadow-sm border border-slate-100">
                                                        <img src={img.url} alt={`Gallery ${i}`} className="w-full h-full object-cover" />
                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                            <button onClick={() => handleDeleteGalleryImage(img.id)} disabled={loading} className="p-3 bg-rose-500 text-white rounded-full hover:scale-110 transition-transform shadow-xl">
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            {(petGallery || []).length === 0 && (
                                                <div className="py-10 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] text-center">
                                                    <Camera size={32} className="mx-auto text-slate-300 mb-2" />
                                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">No hay imágenes en la galería</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'dashboard' && (
                        <BreederHome
                            profile={breederProfile}
                            inventory={inventory}
                            litters={litters}
                            heats={heats}
                            transfers={transfers}
                            planInfo={planInfo}
                            asUser={asUser}
                            isElite={isElite}
                            publicUrl={publicUrl}
                            linkCopied={linkCopied}
                            onCopyLink={copyPublicUrl}
                            onGo={(tab) => setActiveTab(tab)}
                            onNewPet={() => { setActiveTab('manada'); setNewPetData(EMPTY_NEW_PET); setShowNewPetModal(true); }}
                            onOpenPet={openPetDetail}
                            onUpgrade={() => askPro('Compara los planes y elige el que mejor se ajusta a tu criadero.')}
                        />
                    )}

                    {activeTab === 'manada' && (
                        <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#FEF3E8] border border-orange-100 rounded-[2rem] px-5 py-4">
                            <div className="min-w-0">
                                <p className="text-[9px] font-black text-orange-500 uppercase tracking-widest">Tu página pública</p>
                                <p className="text-sm font-bold text-slate-700 truncate">{publicUrl.replace(/^https?:\/\//, '')}</p>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button onClick={copyPublicUrl} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-orange-100 text-orange-600 rounded-[7px] text-[10px] font-black uppercase tracking-widest hover:bg-orange-50 transition-colors">{linkCopied ? 'Copiado' : <><Copy size={13} /> Copiar</>}</button>
                                <button onClick={() => window.open(publicUrl, '_blank')} className="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-[7px] text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-colors"><ExternalLink size={13} /> Ver</button>
                            </div>
                        </div>
                    )}
                    {activeTab === 'manada' && (
                        <ManadaView
                            pets={inventory}
                            onSelectQrPet={setSelectedQrPet}
                            onTransfer={(pet) => { setTransferPet(pet); setTransferEmail(''); setTransferError(''); setShowTransferModal(true); }}
                            onAdoption={(pet) => { setSelectedAdoptionPet(pet); setShowAdoptionModal(true); }}
                            onAddNew={() => { setNewPetData(EMPTY_NEW_PET); setShowNewPetModal(true); }}
                            onGoToPedigree={(pet) => { setSelectedPetDetail(pet); setActiveTab('pedigree'); }}
                            onViewDetail={openPetDetail}
                        />
                    )}
                    {activeTab === 'litters' && (
                        <BreederLitters
                            pets={inventory}
                            litters={litters}
                            heats={heats}
                            asUser={asUser}
                            onReload={async () => { await Promise.all([loadLitters(), loadInventory(), loadPlan()]); }}
                            onPlanLimit={askPro}
                            onAddPuppy={startPuppyFromLitter}
                            onViewPet={openPetDetail}
                        />
                    )}
                    {activeTab === 'waitlist' && (
                        <BreederWaitlist asUser={asUser} isElite={isElite} slug={breederProfile?.slug} onUpgrade={askElite} />
                    )}
                    {activeTab === 'contracts' && (
                        <BreederContracts asUser={asUser} isElite={isElite} pets={inventory} sellerName={user?.name} onUpgrade={askElite} />
                    )}
                    {activeTab === 'pedigree' && <PedigreeView selectedPet={selectedPetDetail} inventory={inventory} onSelectPet={setSelectedPetDetail} />}
                    {activeTab === 'transfers' && (
                        <div className="bg-white rounded-[2rem] p-8 shadow-xl border border-slate-100">
                            <div className="flex justify-between items-center mb-10">
                                <div>
                                    <h3 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter">Gestión de Traspasos</h3>
                                    <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-1">Control de propiedad digital de la Manada</p>
                                </div>
                                <div className="p-3 bg-sky-50 text-sky-600 rounded-[7px]"><Share2 size={24} /></div>
                            </div>

                            <div className="space-y-4">
                                {transfers.map(t => {
                                    const st = ({
                                        pending: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-600' },
                                        completed: { label: 'Completado', cls: 'bg-emerald-100 text-emerald-600' },
                                        cancelled: { label: 'Cancelado', cls: 'bg-slate-100 text-slate-500' },
                                        rejected: { label: 'Rechazado', cls: 'bg-rose-100 text-rose-600' },
                                        expired: { label: 'Expirado', cls: 'bg-slate-100 text-slate-500' },
                                    } as Record<string, { label: string; cls: string }>)[t.status] || { label: t.status, cls: 'bg-slate-100 text-slate-500' };
                                    return (
                                        <div key={t.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 sm:p-6 bg-slate-50 rounded-[2rem] border border-slate-100 hover:border-sky-200 transition-colors">
                                            <div className="flex items-center gap-4 min-w-0">
                                                <div className="w-12 h-12 bg-white rounded-[7px] flex items-center justify-center text-[#FF6B00] shadow-sm overflow-hidden shrink-0">
                                                    {t.petImage ? <img src={t.petImage} className="w-full h-full object-cover" /> : <Dog size={24} />}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-black text-slate-800 uppercase truncate">{t.petName || 'Ejemplar'}</p>
                                                    <p className="text-[10px] font-bold text-slate-400 truncate">{t.toEmail}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
                                                {t.status === 'pending' && (
                                                    <button onClick={() => navigator.clipboard?.writeText(t.token).catch(() => { })} className="text-left group" title="Copiar código">
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1">Código de traspaso <Copy size={10} className="group-hover:text-sky-500" /></p>
                                                        <p className="text-xs font-mono font-black text-sky-600">{t.token}</p>
                                                    </button>
                                                )}
                                                <span className={`px-4 py-1.5 rounded-full text-[8px] font-black uppercase tracking-widest ${st.cls}`}>{st.label}</span>
                                                {t.status === 'pending' && (
                                                    <button onClick={() => cancelTransfer(t)} className="text-[10px] font-black text-rose-400 hover:text-rose-600 uppercase tracking-widest">Cancelar</button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {transfers.length === 0 && (
                                    <div className="py-14 text-center bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                                        <Share2 size={36} className="mx-auto text-slate-300 mb-3" />
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Aún no has iniciado traspasos</p>
                                        <p className="text-[11px] text-slate-400 font-medium mt-1">Usa el botón "Traspaso" de un ejemplar en la Manada.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {activeTab === 'docs' && (
                        <div className="bg-white rounded-[2rem] p-8 shadow-xl border border-slate-100">
                            <div className="flex justify-between items-center mb-10">
                                <div>
                                    <h3 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter">Documentación Sanitaria</h3>
                                    <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-1">Repositorio oficial del Criadero</p>
                                </div>
                            </div>

                            {/* Validaciones de pedigrí enviadas al equipo */}
                            {validations.length > 0 && (
                                <div className="mb-10">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Validaciones de pedigrí</h4>
                                    <div className="space-y-3">
                                        {validations.map(v => (
                                            <div key={v.id} className="p-4 bg-slate-50 rounded-[7px] border border-slate-100 flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="text-xs font-black text-slate-800 uppercase truncate">{v.pet_name || `Ejemplar ${v.pet_id}`} · <span className="font-mono">{v.pedigree_reg}</span></p>
                                                    {v.admin_notes && <p className="text-[10px] text-slate-500 font-medium mt-0.5 truncate">{v.admin_notes}</p>}
                                                </div>
                                                <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest shrink-0 ${v.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : v.status === 'rejected' ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'}`}>
                                                    {v.status === 'approved' ? 'Aprobada' : v.status === 'rejected' ? 'Rechazada' : 'En revisión'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Documentos de tus ejemplares</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {kennelDocs.map(doc => (
                                    <a key={doc.id} href={doc.file_url} target="_blank" rel="noopener noreferrer"
                                        className="p-5 bg-slate-50 rounded-[7px] border border-slate-100 flex items-center gap-4 group hover:bg-white hover:shadow-xl transition-all">
                                        <div className="p-3 bg-white rounded-[7px] text-slate-400 group-hover:text-rose-500 transition-colors"><FileText size={28} /></div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[9px] font-black text-rose-500 uppercase tracking-widest mb-1">{doc.document_type} · {doc.pet_name}</p>
                                            <p className="text-xs font-bold text-slate-800 truncate">{doc.title}</p>
                                            <p className="text-[10px] text-slate-400 font-medium">{doc.uploaded_at ? new Date(String(doc.uploaded_at).replace(' ', 'T')).toLocaleDateString('es-CL') : ''}</p>
                                        </div>
                                        <ExternalLink size={18} className="text-slate-200 group-hover:text-slate-400 transition-colors shrink-0" />
                                    </a>
                                ))}
                                {kennelDocs.length === 0 && (
                                    <div className="md:col-span-2 py-12 text-center bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                                        <Files size={36} className="mx-auto text-slate-300 mb-3" />
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Sin documentos todavía</p>
                                        <p className="text-[11px] text-slate-400 font-medium mt-1">Súbelos desde la ficha de cada ejemplar → pestaña Documentos.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {activeTab === 'settings' && (
                        <div className="bg-white rounded-[3rem] p-10 shadow-2xl border border-slate-100 animate-in slide-in-from-bottom-8 duration-500">
                            <div className="flex items-center gap-6 mb-12">
                                <div className="p-5 bg-orange-50 text-orange-600 rounded-[2rem] rotate-3 shadow-lg shadow-orange-500/10">
                                    <Settings size={32} />
                                </div>
                                <div>
                                    <h3 className="text-3xl font-black text-slate-800 uppercase italic tracking-tighter">Configuración</h3>
                                    <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-1">Personaliza tu identidad como Criadero Pro</p>
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row gap-6 mb-10">
                                <div className="flex-1 space-y-2">
                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Logo del Criadero</h4>
                                    <div className="flex items-center gap-4">
                                        <div className="w-24 h-24 rounded-[7px] bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200">
                                            {settingsFormData.logoUrl ? <img src={settingsFormData.logoUrl} className="w-full h-full object-cover" /> : <ImageIcon size={32} className="text-slate-300" />}
                                        </div>
                                        <label className="cursor-pointer bg-slate-50 hover:bg-orange-50 text-orange-600 px-4 py-2 rounded-[7px] text-[10px] font-black uppercase tracking-widest border border-slate-200 transition-colors">
                                            <Upload size={14} className="inline mr-2" /> Subir Logo
                                            <input type="file" accept="image/*" className="hidden" onChange={e => handleUploadBreederImage(e, 'logo')} />
                                        </label>
                                    </div>
                                </div>
                                <div className="flex-[2] space-y-2">
                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Banner Público</h4>
                                    <div className="flex flex-col gap-4">
                                        <div className="w-full h-24 rounded-[7px] bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200">
                                            {settingsFormData.bannerUrl ? <img src={settingsFormData.bannerUrl} className="w-full h-full object-cover" /> : <ImageIcon size={32} className="text-slate-300" />}
                                        </div>
                                        <label className="self-start cursor-pointer bg-slate-50 hover:bg-orange-50 text-orange-600 px-4 py-2 rounded-[7px] text-[10px] font-black uppercase tracking-widest border border-slate-200 transition-colors">
                                            <Upload size={14} className="inline mr-2" /> Cambiar Banner
                                            <input type="file" accept="image/*" className="hidden" onChange={e => handleUploadBreederImage(e, 'banner')} />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                <div className="space-y-6">
                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">Información del Kennel</h4>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Nombre del Criadero / Afijo</label>
                                        <input
                                            type="text"
                                            className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold transition-all"
                                            value={settingsFormData.kennelName}
                                            onChange={e => setSettingsFormData({ ...settingsFormData, kennelName: e.target.value })}
                                            placeholder="Ej: Los Laureles Kennel"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">País</label>
                                        <select
                                            className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold transition-all appearance-none"
                                            value={settingsFormData.country}
                                            onChange={e => setSettingsFormData({ ...settingsFormData, country: e.target.value, region: '', comuna: '' })}
                                        >
                                            {Object.keys(LOCATIONS).map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Región</label>
                                            <select
                                                className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold transition-all appearance-none"
                                                value={settingsFormData.region}
                                                onChange={e => setSettingsFormData({ ...settingsFormData, region: e.target.value, comuna: '' })}
                                            >
                                                <option value="">Seleccionar...</option>
                                                {Object.keys(LOCATIONS[settingsFormData.country || 'Chile']?.regions || {}).map(r => <option key={r} value={r}>{r}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Ciudad / Comuna</label>
                                            <select
                                                className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold transition-all appearance-none"
                                                value={settingsFormData.comuna}
                                                onChange={e => setSettingsFormData({ ...settingsFormData, comuna: e.target.value })}
                                            >
                                                <option value="">Seleccionar...</option>
                                                {((LOCATIONS[settingsFormData.country || 'Chile']?.regions || {}) as any)[settingsFormData.region || '']?.map((c: string) => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Razas Especializadas</label>
                                        <input
                                            type="text"
                                            className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold transition-all"
                                            value={settingsFormData.specialty}
                                            onChange={e => setSettingsFormData({ ...settingsFormData, specialty: e.target.value })}
                                            placeholder="Ej: Pastor Alemán, Border Collie"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">Contacto Publico</h4>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Teléfono de Contacto</label>
                                        <input
                                            type="text"
                                            inputMode="tel"
                                            className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold transition-all"
                                            value={settingsFormData.phone}
                                            onChange={e => setSettingsFormData({ ...settingsFormData, phone: e.target.value.replace(/[^\d+]/g, '') })}
                                            placeholder="+56 9 1234 5678"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">{(LOCATIONS[settingsFormData.country || 'Chile'] || LOCATIONS['Chile']).idName}</label>
                                        <input
                                            type="text"
                                            className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold transition-all"
                                            value={settingsFormData.manager_rut}
                                            onChange={e => setSettingsFormData({ ...settingsFormData, manager_rut: formatGenericID(e.target.value, settingsFormData.country || 'Chile') })}
                                            placeholder={(LOCATIONS[settingsFormData.country || 'Chile'] || LOCATIONS['Chile']).idPlaceholder}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Email del Criadero</label>
                                        <input
                                            type="email"
                                            className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold transition-all"
                                            value={settingsFormData.email}
                                            onChange={e => setSettingsFormData({ ...settingsFormData, email: e.target.value })}
                                            placeholder="contacto@mi-criadero.cl"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Descripción / Misión</label>
                                        <textarea
                                            rows={3}
                                            className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold transition-all resize-none"
                                            value={settingsFormData.description}
                                            onChange={e => setSettingsFormData({ ...settingsFormData, description: e.target.value })}
                                            placeholder="Dedicados a la crianza responsable y preservación de..."
                                        ></textarea>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-12 flex justify-end">
                                <button
                                    onClick={handleSaveSettings}
                                    disabled={loading}
                                    className="bg-[#FF6B00] text-white px-10 py-5 rounded-[2rem] font-black uppercase text-xs tracking-widest shadow-2xl shadow-orange-500/20 hover:scale-105 transition-transform flex items-center gap-3 disabled:opacity-50"
                                >
                                    {loading ? 'Guardando...' : <><Save size={18} /> Guardar Configuración</>}
                                </button>
                            </div>
                        </div>
                    )}
                </main>
            </div >

            {upgradeReason !== null && <BreederUpgradeModal reason={upgradeReason || undefined} need={upgradeNeed} onClose={() => { setUpgradeReason(null); setUpgradeNeed('pro'); }} />}

            {/* Validation Request Modal */}
            {
                showValidationRequestModal && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-amber-950/90 backdrop-blur-xl p-4">
                        <div className="bg-white rounded-[3rem] p-6 sm:p-10 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                            <div className="text-center mb-8">
                                <div className="w-20 h-20 bg-amber-100 text-amber-600 rounded-[7px] flex items-center justify-center mx-auto mb-6 rotate-6 shadow-lg">
                                    <Award size={40} />
                                </div>
                                <h3 className="text-2xl font-black text-slate-800 uppercase italic">Solicitud de Certificación</h3>
                                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-2">La validación final la realiza el equipo de Amigo Peludo</p>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">N° Registro Kennel Club (KCC)</label>
                                    <input
                                        className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-amber-500 font-black text-amber-600 uppercase tracking-widest"
                                        placeholder="EJ: KCC-123.456"
                                        value={validationReqData.pedigreeReg}
                                        onChange={e => setValidationReqData({ ...validationReqData, pedigreeReg: e.target.value.toUpperCase() })}
                                    />
                                </div>

                                <div
                                    className="p-6 border-2 border-dashed border-slate-200 rounded-[7px] text-center hover:border-amber-500 transition-colors cursor-pointer group relative"
                                    onClick={() => document.getElementById('validation-doc-upload')?.click()}
                                >
                                    <input
                                        id="validation-doc-upload"
                                        type="file"
                                        accept="image/*,application/pdf"
                                        className="hidden"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                setValidationReqData({ ...validationReqData, document: e.target.files[0] });
                                            }
                                        }}
                                    />
                                    {validationReqData.document ? (
                                        <>
                                            <CheckCircle size={32} className="text-emerald-500 mx-auto mb-3" />
                                            <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest leading-tight">{validationReqData.document.name}</p>
                                            <p className="text-[8px] font-bold text-slate-300 uppercase mt-1 italic">Click para cambiar</p>
                                        </>
                                    ) : (
                                        <>
                                            <FileText size={32} className="text-slate-300 mx-auto mb-3 group-hover:text-amber-500 transition-colors" />
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-tight group-hover:text-slate-600">Adjuntar Certificado PDF / Imagen</p>
                                            <p className="text-[8px] font-bold text-slate-300 uppercase mt-1 italic">Max: 5MB</p>
                                        </>
                                    )}
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        onClick={() => setShowValidationRequestModal(false)}
                                        className="flex-1 bg-slate-100 text-slate-500 py-4 rounded-[7px] font-black uppercase text-xs hover:bg-slate-200 transition-all"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        disabled={isSubmittingValidation || !validationReqData.pedigreeReg}
                                        onClick={handleSubmitValidation}
                                        className="flex-1 bg-amber-500 text-white py-4 rounded-[7px] font-black uppercase text-xs shadow-xl shadow-amber-500/20 animate-pulse-slow disabled:opacity-50 hover:bg-amber-600 hover:scale-105 transition-all"
                                    >
                                        {isSubmittingValidation ? 'Enviando...' : 'Enviar Solicitud'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* Success Modal */}
            {
                successModal.isOpen && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={() => setSuccessModal({ ...successModal, isOpen: false })}>
                        <div className="bg-white rounded-[3rem] p-10 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-500 text-center relative overflow-hidden" onClick={e => e.stopPropagation()}>
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-400 via-amber-400 to-rose-400"></div>
                            <div className="w-24 h-24 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl animate-bounce-slow">
                                <CheckCircle size={48} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-800 uppercase italic mb-2 leading-tight">{successModal.title}</h3>
                            <p className="text-xs font-bold text-slate-500 leading-relaxed mb-8" dangerouslySetInnerHTML={{ __html: successModal.message }}></p>
                            <button
                                onClick={() => setSuccessModal({ ...successModal, isOpen: false })}
                                className="w-full bg-[#FF6B00] text-white py-4 rounded-[7px] font-black uppercase text-xs shadow-lg hover:bg-[#E65C00] transition-all hover:scale-105"
                            >
                                ¡Entendido!
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Vaccine Record Modal */}
            {
                showVaccineModal && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-sky-950/90 backdrop-blur-xl p-4">
                        <div className="bg-white rounded-[3.5rem] p-6 sm:p-10 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                            <div className="text-center mb-8">
                                <div className="w-20 h-20 bg-sky-100 text-sky-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 rotate-6 shadow-xl">
                                    <Syringe size={40} />
                                </div>
                                <h3 className="text-2xl font-black text-slate-800 uppercase italic">Registrar Vacuna</h3>
                                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-2">Ingresa los datos para {selectedPetDetail?.name}</p>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nombre de Vacuna / Desparasitante</label>
                                    <input
                                        className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-sky-500 font-bold text-sky-600"
                                        placeholder="EJ: Sextuple (Refuerzo)"
                                        value={healthFormData.name || ''}
                                        onChange={e => setHealthFormData({ ...healthFormData, name: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Fecha de Aplicación</label>
                                    <input
                                        type="date"
                                        className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-sky-500 font-bold"
                                        value={healthFormData.date || ''}
                                        onChange={e => setHealthFormData({ ...healthFormData, date: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Clínica / Lugar</label>
                                    <input
                                        className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-sky-500 font-bold"
                                        placeholder="EJ: Veterinaria San Roque"
                                        value={healthFormData.place || ''}
                                        onChange={e => setHealthFormData({ ...healthFormData, place: e.target.value })}
                                    />
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        onClick={() => setShowVaccineModal(false)}
                                        className="flex-1 bg-slate-100 text-slate-500 py-4 rounded-[7px] font-black uppercase text-xs"
                                    >
                                        Cerrar
                                    </button>
                                    <button
                                        onClick={handleAddVaccine}
                                        className="flex-1 bg-sky-600 text-white py-4 rounded-[7px] font-black uppercase text-xs shadow-lg"
                                    >
                                        Guardar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Add Pet Document Modal */}
            {
                showAddDocModal && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-rose-950/90 backdrop-blur-xl p-4">
                        <div className="bg-white rounded-[3.5rem] p-6 sm:p-10 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                            <div className="text-center mb-8">
                                <div className="w-20 h-20 bg-rose-100 text-rose-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 rotate-6 shadow-xl">
                                    <Files size={40} />
                                </div>
                                <h3 className="text-2xl font-black text-slate-800 uppercase italic">Subir Documento</h3>
                                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-2">Expediente oficial de {selectedPetDetail?.name}</p>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Título del Documento</label>
                                    <input
                                        className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-rose-500 font-bold"
                                        placeholder="EJ: Certificado de Displasia"
                                        value={docFormData.title}
                                        onChange={e => setDocFormData({ ...docFormData, title: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Categoría</label>
                                    <select
                                        className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-[7px] outline-none focus:border-rose-500 font-bold"
                                        value={docFormData.type}
                                        onChange={e => setDocFormData({ ...docFormData, type: e.target.value })}
                                    >
                                        <option value="Pedigree">Pedigrí / Certificado Origen</option>
                                        <option value="Examen de Salud">Examen de Salud</option>
                                        <option value="ADN">Perfil de ADN</option>
                                        <option value="Microchip">Certificado Microchip</option>
                                        <option value="Contrato">Contrato / Otros</option>
                                    </select>
                                </div>

                                <div
                                    className="p-8 border-2 border-dashed border-slate-200 rounded-[2.5rem] text-center hover:border-rose-500 transition-colors cursor-pointer group relative bg-slate-50"
                                    onClick={() => document.getElementById('pet-doc-upload')?.click()}
                                >
                                    <input
                                        id="pet-doc-upload"
                                        type="file"
                                        accept="image/*,application/pdf"
                                        className="hidden"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                setDocFormData({ ...docFormData, file: e.target.files[0] });
                                            }
                                        }}
                                    />
                                    {docFormData.file ? (
                                        <>
                                            <CheckCircle size={32} className="text-emerald-500 mx-auto mb-3" />
                                            <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">{docFormData.file.name}</p>
                                        </>
                                    ) : (
                                        <>
                                            <Upload size={32} className="text-slate-300 mx-auto mb-3 group-hover:text-rose-500 transition-colors" />
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-tight">Seleccionar archivo PDF o Imagen</p>
                                        </>
                                    )}
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        onClick={() => setShowAddDocModal(false)}
                                        className="flex-1 bg-slate-100 text-slate-500 py-4 rounded-[7px] font-black uppercase text-xs"
                                    >
                                        Cerrar
                                    </button>
                                    <button
                                        disabled={loading || !docFormData.file || !docFormData.title}
                                        onClick={handleUploadDocument}
                                        className="flex-1 bg-rose-600 text-white py-4 rounded-[7px] font-black uppercase text-xs shadow-lg disabled:opacity-50"
                                    >
                                        {loading ? 'Subiendo...' : 'Guardar Documento'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Consultation Record Modal */}
            {
                showConsultationModal && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-violet-950/90 backdrop-blur-xl p-4">
                        <div className="bg-white rounded-[3rem] w-full max-w-lg shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                            <div className="bg-violet-600 p-8 text-center relative overflow-hidden shrink-0">
                                <div className="absolute -left-2 -bottom-2 text-white/10 rotate-12"><Stethoscope size={80} /></div>
                                <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter relative z-10">{editingConsultId ? 'Editar Atención' : 'Registrar Atención'}</h3>
                                <p className="text-white/80 text-[10px] font-bold uppercase tracking-widest mt-1 relative z-10">{editingConsultId ? `Editando registro de ${selectedPetDetail?.name}` : `Nuevo registro médico para ${selectedPetDetail?.name}`}</p>
                                <button onClick={() => { setShowConsultationModal(false); setEditingConsultId(null); }} className="absolute top-6 right-6 text-white/80 hover:text-white p-2">
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="p-8 space-y-6 overflow-y-auto flex-1">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Tipo</label>
                                        <select
                                            className={`w-full bg-slate-50 border-2 rounded-[7px] px-4 py-3 font-bold text-slate-700 outline-none transition-all ${consultationErrors.type ? 'border-rose-500 bg-rose-50' : 'border-slate-100 focus:border-violet-500'}`}
                                            value={healthFormData.type || 'Control'}
                                            onChange={(e) => {
                                                setHealthFormData({ ...healthFormData, type: e.target.value });
                                                if (consultationErrors.type) setConsultationErrors({ ...consultationErrors, type: false });
                                            }}
                                        >
                                            <option value="Control">Control</option>
                                            <option value="Urgencia">Urgencia</option>
                                            <option value="Vacunación">Vacunación</option>
                                            <option value="Cirugía">Cirugía</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Fecha</label>
                                        <input
                                            type="date"
                                            className={`w-full bg-slate-50 border-2 rounded-[7px] px-4 py-3 font-bold text-slate-700 outline-none transition-all ${consultationErrors.date ? 'border-rose-500 bg-rose-50' : 'border-slate-100 focus:border-violet-500'}`}
                                            value={healthFormData.date || ''}
                                            onChange={(e) => {
                                                setHealthFormData({ ...healthFormData, date: e.target.value });
                                                if (consultationErrors.date) setConsultationErrors({ ...consultationErrors, date: false });
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Lugar / Clínica</label>
                                    <input
                                        type="text"
                                        placeholder="Veterinaria..."
                                        className={`w-full bg-slate-50 border-2 rounded-[7px] px-4 py-3 font-bold text-slate-700 outline-none transition-all ${consultationErrors.place ? 'border-rose-500 bg-rose-50' : 'border-slate-100 focus:border-violet-500'}`}
                                        value={healthFormData.place || ''}
                                        onChange={(e) => {
                                            setHealthFormData({ ...healthFormData, place: e.target.value });
                                            if (consultationErrors.place) setConsultationErrors({ ...consultationErrors, place: false });
                                        }}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Médico Profesional</label>
                                    <input
                                        type="text"
                                        placeholder="Nombre del veterinario..."
                                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-[7px] px-4 py-3 font-bold text-slate-700 outline-none focus:border-violet-500 transition-all"
                                        value={healthFormData.doctor || ''}
                                        onChange={(e) => setHealthFormData({ ...healthFormData, doctor: e.target.value })}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Diagnóstico</label>
                                    <textarea
                                        placeholder="¿Qué tiene la mascota?"
                                        rows={2}
                                        className={`w-full bg-slate-50 border-2 rounded-[7px] px-4 py-2 font-bold text-slate-700 outline-none transition-all resize-none text-xs ${consultationErrors.diagnosis ? 'border-rose-500 bg-rose-50' : 'border-slate-100 focus:border-violet-500'}`}
                                        value={healthFormData.diagnosis || ''}
                                        onChange={(e) => {
                                            setHealthFormData({ ...healthFormData, diagnosis: e.target.value });
                                            if (consultationErrors.diagnosis) setConsultationErrors({ ...consultationErrors, diagnosis: false });
                                        }}
                                    ></textarea>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Tratamiento</label>
                                    <textarea
                                        placeholder="Recomendaciones..."
                                        rows={2}
                                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-[7px] px-4 py-2 font-bold text-slate-700 outline-none focus:border-violet-500 transition-all resize-none text-xs"
                                        value={healthFormData.recommendation || ''}
                                        onChange={(e) => setHealthFormData({ ...healthFormData, recommendation: e.target.value })}
                                    ></textarea>
                                </div>

                                <div className="space-y-1 mt-2">
                                    <label className="text-[10px] font-black text-violet-600 uppercase tracking-widest ml-2">Medicamentos (Opcional)</label>
                                    <div className="bg-slate-50 p-4 rounded-[7px] space-y-3 border border-slate-200">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="col-span-2">
                                                <input type="text" placeholder="Medicamento" className="w-full bg-white border border-slate-200 rounded-[7px] px-4 py-2 text-xs font-bold outline-none" value={prescriptionForm.name} onChange={(e) => setPrescriptionForm({ ...prescriptionForm, name: e.target.value })} />
                                            </div>
                                            <div>
                                                <label className="text-[8px] font-black text-slate-400 uppercase ml-2">Cada (Hrs)</label>
                                                <input type="text" inputMode="numeric" placeholder="8" className="w-full bg-white border border-slate-200 rounded-[7px] px-4 py-2 text-xs font-bold outline-none" value={prescriptionForm.freq} onChange={(e) => setPrescriptionForm({ ...prescriptionForm, freq: e.target.value.replace(/\D/g, '') })} />
                                            </div>
                                            <div>
                                                <label className="text-[8px] font-black text-slate-400 uppercase ml-2">Días</label>
                                                <input type="text" inputMode="numeric" placeholder="5" className="w-full bg-white border border-slate-200 rounded-[7px] px-4 py-2 text-xs font-bold outline-none" value={prescriptionForm.duration} onChange={(e) => setPrescriptionForm({ ...prescriptionForm, duration: e.target.value.replace(/\D/g, '') })} />
                                            </div>
                                        </div>
                                        <button onClick={handleAddPrescription} className="w-full py-2 bg-violet-50 text-violet-600 rounded-[7px] text-[10px] font-black uppercase hover:bg-violet-100 transition-all flex items-center justify-center gap-2">
                                            <Plus size={14} strokeWidth={3} /> Agregar Receta
                                        </button>

                                        {tempPrescriptions.length > 0 && (
                                            <div className="space-y-2 pt-2 border-t border-slate-200">
                                                {tempPrescriptions.map((p: any) => (
                                                    <div key={p.id} className="flex justify-between items-center bg-white p-3 rounded-[7px] border border-slate-100 shadow-sm">
                                                        <div>
                                                            <p className="text-[10px] font-black text-slate-700 uppercase">{p.name}</p>
                                                            <p className="text-[8px] text-slate-400 font-bold uppercase">C/{p.freq} hrs por {p.duration} días</p>
                                                        </div>
                                                        <button onClick={() => handleRemovePrescription(p.id)} className="text-rose-400 hover:text-rose-600 p-1">
                                                            <X size={16} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4 p-8 bg-slate-50 shrink-0">
                                <button
                                    onClick={() => { setShowConsultationModal(false); setEditingConsultId(null); }}
                                    className="flex-1 bg-white border-2 border-slate-200 text-slate-400 py-4 rounded-[7px] font-black uppercase text-xs hover:bg-slate-100 transition-all"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleAddConsultation}
                                    className="flex-1 bg-violet-600 text-white py-4 rounded-[7px] font-black uppercase text-xs shadow-xl hover:bg-violet-700 hover:scale-105 transition-all"
                                >
                                    {editingConsultId ? 'Guardar Cambios' : 'Guardar Atención'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

        </div>
    );
};